<template>
  <div class="concessions-page">
    <div class="page-header">
      <PageHeaderPortal>
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-hand-holding-dollar"></i>
        </div>
        <div>
          <h1 class="page-title">Concessions</h1>
          <p class="page-subtitle">Price exception requests and approvals</p>
        </div>
      </div>
      </PageHeaderPortal>

      <div class="header-actions">
        <select v-if="tab === 'dashboard'" v-model="days" class="filter-select days-select" @change="loadRequests">
          <option :value="7">Last 7 Days</option>
          <option :value="30">Last 30 Days</option>
          <option :value="90">Last 90 Days</option>
          <option :value="365">Last Year</option>
        </select>

        <!-- Read-only view of what the importer has already loaded. Separate button from the
             importer so someone can check who is on the roster without being anywhere near
             the upload surface — reviewing is the common job, importing is the rare one. -->
        <button v-if="context.is_concessions_admin"
                class="approvers-btn"
                @click="openApproverRoster"
                title="View Concession Approvers">
          <i class="fa-duotone fa-address-book"></i>
        </button>

        <!-- The roster and ceilings this imports are concession data, so the importer lives
             with them rather than on the Lock Desk Queue page it started on. -->
        <button v-if="context.is_concessions_admin"
                class="approvers-btn"
                @click="openApproverImport"
                title="Import Concession Approvers">
          <i class="fa-duotone fa-file-import"></i>
        </button>

        <button class="refresh-btn" :disabled="loading" @click="reload">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Request / Dashboard switch. The dashboard only exists for approvers and
         lock desk staff; everyone else just sees their own history there. -->
    <div class="tab-bar">
      <button class="tab-btn" :class="{ 'is-active': tab === 'request' }" @click="switchToRequest">
        <i class="fa-duotone fa-file-circle-plus"></i> Request
      </button>
      <button class="tab-btn" :class="{ 'is-active': tab === 'dashboard' }" @click="switchToDashboard">
        <i class="fa-duotone fa-table-rows"></i>
        {{ context.is_concessions_admin
             ? 'All Concessions'
             : (context.is_approver ? 'Approvals' : 'My Requests') }}
        <span v-if="pendingForMe.length" class="tab-badge">{{ pendingForMe.length }}</span>
      </button>
      <button class="tab-btn" :class="{ 'is-active': tab === 'audit' }" @click="switchToAudit">
        <i class="fa-duotone fa-clipboard-list-check"></i> Audit
      </button>
      <!-- Admin only: shows routing and recipient addresses across every division. -->
      <button v-if="context.is_concessions_admin"
              class="tab-btn" :class="{ 'is-active': tab === 'diagnostics' }"
              @click="switchToDiagnostics">
        <i class="fa-duotone fa-stethoscope"></i> Polly Intake
        <span v-if="pollyDryRunCount" class="tab-badge">{{ pollyDryRunCount }}</span>
      </button>
    </div>

    <div v-if="contextLoading" class="state-card">Loading your access...</div>

    <!-- ══════════════════════════ REQUEST ══════════════════════════ -->
    <!-- The locked-loan pipeline. Picking a loan opens the request in its OWN BROWSER
         TAB (ConcessionRequest.vue, route /concessions/request/:loanId) rather than a
         panel under the grid — so several concessions can be worked side by side and
         the form never competes with 400 rows for the screen.

         The manual path is the same page without a loan id, for a loan the pipeline
         cannot offer: a lock Reporting hasn't picked up yet, or someone else's loan
         that lock desk staff need to file against. -->
    <template v-else-if="tab === 'request'">
      <!-- The form is NOT gated — anyone signed in may file one, and the
           approvers are the gate. roster_note is informational: it says what
           will happen to what you file, it does not stop you filing it. -->
      <div v-if="context.roster_note" class="roster-note-card">
        <div class="roster-note-icon"><i class="fa-duotone fa-circle-info"></i></div>
        <div class="roster-note-text">{{ context.roster_note }}</div>
      </div>

      <!-- ── the locked-loan pipeline ── -->
      <!-- The grid is the whole tab now: picking a loan opens the request in its own
           browser tab, so several concessions can be worked at once and the form is
           never competing with 400 rows for the screen. -->
      <div class="filter-row pipeline-filters">
          <select v-if="pipeline.available_scopes.length > 1" v-model="pipelineScope"
                  class="filter-select" @change="onPipelineScopeChange">
            <option v-for="s in pipeline.available_scopes" :key="s" :value="s">
              {{ scopeLabel(s) }}
            </option>
          </select>

          <!-- Server-side, unlike the filters below: search is what reaches past
               the row cap on the rare set big enough to hit it. -->
          <input v-model.trim="pipelineSearch" class="filter-select audit-search" type="text"
                 placeholder="Loan number, borrower or loan officer..."
                 @input="onPipelineSearch" @keyup.enter="loadPipeline" />

          <button class="refresh-btn" :disabled="pipelineLoading" @click="loadPipeline">
            {{ pipelineLoading ? 'Loading...' : 'Refresh' }}
          </button>

          <!-- A real link, not a button: middle-click and ctrl-click have to behave. -->
          <a class="manual-link" :href="manualRequestUrl" target="_blank" rel="noopener">
            Loan not listed? Enter it manually
          </a>
        </div>

        <!-- Filtered client-side. The whole set is loaded (a few hundred rows), so
             this is instant and, unlike a filter over a capped page, it cannot
             quietly hide matches that never came down. Options are built from the
             rows actually present, so a filter can never return nothing. -->
        <div v-if="pipeline.loans.length" class="filter-row pipeline-filters">
          <select v-model="pipelineFilters.expiry" class="filter-select">
            <option value="">Any lock expiry</option>
            <option value="expired">Already expired</option>
            <option value="7">Expires within 7 days</option>
            <option value="14">Expires within 14 days</option>
            <option value="30">Expires within 30 days</option>
            <option value="none">No expiry on file</option>
          </select>

          <select v-model="pipelineFilters.concessions" class="filter-select">
            <option value="">Any concessions</option>
            <option value="none">Nothing on the file</option>
            <option value="some">Something on the file</option>
            <option value="pending">Has one pending here</option>
          </select>

          <select v-if="pipelineFacets.products.length > 1"
                  v-model="pipelineFilters.product" class="filter-select">
            <option value="">Any product</option>
            <option v-for="p in pipelineFacets.products" :key="p.value" :value="p.value">
              {{ p.label }}
            </option>
          </select>

          <select v-if="pipelineFacets.milestones.length > 1"
                  v-model="pipelineFilters.milestone" class="filter-select">
            <option value="">Any milestone</option>
            <option v-for="m in pipelineFacets.milestones" :key="m" :value="m">{{ m }}</option>
          </select>

          <select v-if="pipelineScope !== 'mine' && pipelineFacets.officers.length > 1"
                  v-model="pipelineFilters.officer" class="filter-select">
            <option value="">Any loan officer</option>
            <option v-for="o in pipelineFacets.officers" :key="o" :value="o">{{ o }}</option>
          </select>

          <button v-if="pipelineFiltersActive" class="link-btn" @click="clearPipelineFilters">
            Clear filters
          </button>
        </div>

        <div v-if="!pipelineLoading && pipeline.loans.length && !visiblePipelineLoans.length"
             class="state-card">
          No locked loans match these filters.
          <button class="link-btn" @click="clearPipelineFilters">Clear filters</button>
        </div>

        <div v-if="pipelineError" class="blocked-card">
          <div class="blocked-icon"><i class="fa-duotone fa-circle-exclamation"></i></div>
          <div>
            <div class="blocked-title">Couldn't load {{ pipelineScopeNoun }}</div>
            <div class="blocked-text">{{ pipelineError }}</div>
            <div class="blocked-text">
              You can still
              <a :href="manualRequestUrl" target="_blank" rel="noopener">file one manually</a>.
            </div>
          </div>
        </div>

        <div class="audit-count">
          <span v-if="pipelineLoading">Loading {{ pipelineScopeNoun }}...</span>
          <template v-else>
            <span v-if="pipeline.note">{{ pipeline.note }}</span>
            <span v-else-if="pipelineFiltersActive">
              Showing {{ visiblePipelineLoans.length }} of {{ pipeline.loans.length }}
              locked {{ pipeline.loans.length === 1 ? 'loan' : 'loans' }}
            </span>
            <span v-else-if="pipeline.loans.length">
              {{ pipeline.loans.length }} locked
              {{ pipeline.loans.length === 1 ? 'loan' : 'loans' }}
            </span>
          </template>
        </div>

        <div v-if="!pipelineLoading && visiblePipelineLoans.length" ref="pipelineTable" class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th class="sortable" @click="sortPipelineBy('loan_number')">
                  Loan <span class="sort-caret">{{ sortCaret('loan_number') }}</span>
                </th>
                <th class="sortable" @click="sortPipelineBy('borrower')">
                  Borrower <span class="sort-caret">{{ sortCaret('borrower') }}</span>
                </th>
                <th v-if="pipelineScope !== 'mine'" class="sortable"
                    @click="sortPipelineBy('loan_officer_name')">
                  Loan officer <span class="sort-caret">{{ sortCaret('loan_officer_name') }}</span>
                </th>
                <th class="num sortable" @click="sortPipelineBy('loan_amount')">
                  Loan amount <span class="sort-caret">{{ sortCaret('loan_amount') }}</span>
                </th>
                <th class="num sortable" @click="sortPipelineBy('interest_rate')">
                  Rate <span class="sort-caret">{{ sortCaret('interest_rate') }}</span>
                </th>
                <th class="sortable" @click="sortPipelineBy('product')">
                  Product <span class="sort-caret">{{ sortCaret('product') }}</span>
                </th>
                <th class="sortable" @click="sortPipelineBy('lock_expires')">
                  Lock expires <span class="sort-caret">{{ sortCaret('lock_expires') }}</span>
                </th>
                <th class="num sortable" @click="sortPipelineBy('concession_on_file_amount')">
                  Concessions on file
                  <span class="sort-caret">{{ sortCaret('concession_on_file_amount') }}</span>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in visiblePipelineLoans" :key="row.loan_id"
                  class="pipeline-row"
                  @click="openRequest(row)">
                <td class="nowrap">
                  {{ row.loan_number || '—' }}
                  <div v-if="row.loan_program" class="sub-note">{{ row.loan_program }}</div>
                </td>
                <td>{{ borrowerName(row) }}</td>
                <td v-if="pipelineScope !== 'mine'" class="wrap-email">
                  {{ row.loan_officer_name || row.loan_officer_email || '—' }}
                </td>
                <td class="num">{{ formatMoney(row.loan_amount) }}</td>
                <td class="num">{{ formatRate(row.interest_rate) }}</td>
                <td>
                  {{ row.product_type ? productLabel(row.product_type) : (row.product_type_raw || '—') }}
                  <!-- An unmapped product cannot pick a ceiling, so the form will
                       make the user choose one. Flagged here so that is no surprise. -->
                  <span v-if="!row.product_type && row.product_type_raw" class="diag-flag is-warn">
                    unmapped
                  </span>
                </td>
                <td class="nowrap">
                  {{ formatLoanDate(row.lock_expires) }}
                  <div v-if="lockExpiryLabel(row)" class="sub-note"
                       :class="{ 'is-warn': isLockUrgent(row) }">
                    {{ lockExpiryLabel(row) }}
                  </div>
                </td>
                <!-- The Encompass total is what is actually ON the file. Anything
                     still pending here is NOT, which is exactly why it is worth
                     saying underneath rather than folding into the number. -->
                <!-- bps, not dollars: ceilings are set in bps, so this is the number
                     that can be compared against them at a glance. -->
                <td class="num">
                  <div :class="{ 'conc-zero': !row.concession_on_file_amount }">
                    {{ bpsLabel(row.concession_on_file_bps, row.concession_on_file_amount) }}
                  </div>
                  <div v-if="row.swift_pending_count" class="sub-note is-warn">
                    {{ row.swift_pending_count }} pending here<span
                      v-if="pendingBps(row) !== null"> · {{ formatNumber(pendingBps(row)) }} bps</span>
                  </div>
                </td>
                <td class="actions-cell">
                  <!-- An anchor, so middle-click, ctrl-click and "open in new tab" all
                       do what the user expects. @click.stop keeps the row handler from
                       opening a second copy. -->
                  <a class="approve-btn" :href="requestUrl(row)" target="_blank"
                     rel="noopener" @click.stop>Request</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
    </template>

    <!-- ══════════════════════════ DASHBOARD ══════════════════════════ -->
    <template v-else-if="tab === 'dashboard'">
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-value">{{ visibleRequests.length }}</div>
          <div class="summary-label">Total</div>
        </div>
        <div class="summary-card is-pending">
          <div class="summary-value">{{ countBy('pending') }}</div>
          <div class="summary-label">Pending</div>
        </div>
        <div class="summary-card is-approved">
          <div class="summary-value">{{ countBy('auto_approved') + countBy('approved') }}</div>
          <div class="summary-label">Approved</div>
        </div>
        <div class="summary-card is-rejected">
          <div class="summary-value">{{ countBy('rejected') }}</div>
          <div class="summary-label">Rejected</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">{{ avgBps }}</div>
          <div class="summary-label">Avg bps</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">{{ totalDollars }}</div>
          <div class="summary-label">Total given</div>
        </div>
        <div class="summary-card is-warn" v-if="pollyProblems.length">
          <div class="summary-value">{{ pollyProblems.length }}</div>
          <div class="summary-label">Not in Polly</div>
        </div>
      </div>

      <div class="filter-row">
        <select v-model="statusFilter" class="filter-select" @change="loadRequests">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="auto_approved">Auto-approved</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select v-if="filterDivisions.length > 1" v-model="divisionFilter"
                class="filter-select" @change="loadRequests">
          <option value="">{{ context.is_concessions_admin ? 'All divisions' : 'All my divisions' }}</option>
          <option v-for="d in filterDivisions" :key="d" :value="d">{{ d }}</option>
        </select>

        <!-- Filtered client-side: the loaded window is already bounded, and this
             is what the pipeline's "view them" link sets to land you on one
             loan's history. -->
        <input v-model.trim="loanFilter" class="filter-select audit-search"
               type="text" placeholder="Loan number..." />

        <button v-if="loanFilter" class="link-btn" @click="loanFilter = ''">
          Clear loan filter
        </button>
      </div>

      <div v-if="pollyProblems.length" class="warn-banner">
        <i class="fa-duotone fa-triangle-exclamation"></i>
        <span>
          {{ pollyProblems.length }}
          {{ pollyProblems.length === 1 ? 'concession is' : 'concessions are' }}
          approved here but not yet accepted by Polly. They are recorded, but the price exception
          does not exist on the loan until Polly takes it.
        </span>
      </div>

      <div v-if="loading" class="state-card">Loading...</div>
      <div v-else-if="!visibleRequests.length" class="state-card">
        <span v-if="loanFilter">
          No concessions on loan {{ loanFilter }} in this window.
          <button class="link-btn" @click="loanFilter = ''">Clear the loan filter</button>
        </span>
        <span v-else>No concessions in this window.</span>
      </div>

      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>Loan</th>
              <th>Borrower</th>
              <th>Requested by</th>
              <th>Division</th>
              <th>Concession type</th>
              <th>Product</th>
              <th class="num">bps</th>
              <th class="num">$</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Polly</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in visibleRequests" :key="r.id">
              <tr>
                <td class="nowrap">{{ formatDateTime(r.created_utc) }}</td>
                <td class="nowrap">{{ r.loan_number || '—' }}</td>
                <td>{{ borrowerName(r) }}</td>
                <td class="wrap-email">{{ r.requested_by_name || r.requested_by_email }}</td>
                <td>{{ r.division || '—' }}</td>
                <td>{{ r.tier === 'corp' ? 'Corporate' : 'Branch' }}</td>
                <td>{{ productLabel(r.product_type) }}</td>
                <td class="num">{{ formatNumber(r.requested_bps) }}</td>
                <td class="num">{{ formatMoney(r.requested_amount) }}</td>
                <td>{{ r.reason_text || r.reason_code || '—' }}</td>
                <td>
                  <span class="pill" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
                  <div v-if="r.decided_by_name" class="sub-note">by {{ r.decided_by_name }}</div>
                </td>
                <td>
                  <span class="pill" :class="pollyClass(r.polly_sync_status)">
                    {{ pollyLabel(r.polly_sync_status) }}
                  </span>
                </td>
                <td class="actions-cell">
                  <button class="link-btn" @click="toggleDetail(r)">
                    {{ expandedId === r.id ? 'Hide' : 'Details' }}
                  </button>
                  <button v-if="r.status === 'pending'" class="approve-btn"
                          :disabled="decidingId === r.id" @click="openDecision(r, 'approve')">
                    Approve
                  </button>
                  <button v-if="r.status === 'pending'" class="reject-btn"
                          :disabled="decidingId === r.id" @click="openDecision(r, 'reject')">
                    Reject
                  </button>
                </td>
              </tr>

              <tr v-if="expandedId === r.id" class="detail-row">
                <td colspan="13">
                  <div class="detail-grid">
                    <div>
                      <div class="detail-title">Decision</div>
                      <div class="detail-line">Rule: <code>{{ r.matched_rule || '—' }}</code></div>
                      <div class="detail-line">
                        Ceiling applied:
                        {{ r.threshold_bps_applied === null || r.threshold_bps_applied === undefined
                           ? 'none configured (auto-approve)'
                           : formatNumber(r.threshold_bps_applied) + ' bps' }}
                      </div>
                      <div class="detail-line">Routing: {{ routingLabel(r.routing) }}</div>
                      <div v-if="r.decision_notes" class="detail-line">Notes: {{ r.decision_notes }}</div>
                    </div>
                    <div>
                      <div class="detail-title">Polly</div>
                      <div class="detail-line">Status: {{ pollyLabel(r.polly_sync_status) }}</div>
                      <div v-if="r.polly_pe_request_id" class="detail-line">
                        peRequestId: <code>{{ r.polly_pe_request_id }}</code>
                      </div>
                      <div v-if="r.polly_sync_attempts" class="detail-line">
                        Attempts: {{ r.polly_sync_attempts }}
                      </div>
                      <div v-if="r.polly_sync_last_error" class="detail-line is-warn">
                        {{ r.polly_sync_last_error }}
                      </div>
                    </div>
                    <div>
                      <div class="detail-title">Requester notes</div>
                      <div class="detail-line">{{ r.requester_notes || '—' }}</div>
                    </div>
                    <div>
                      <div class="detail-title">Audit trail</div>
                      <div v-if="eventsLoading" class="detail-line">Loading...</div>
                      <div v-else-if="!events.length" class="detail-line">No events.</div>
                      <div v-for="e in events" :key="e.id" class="detail-line">
                        <span class="event-when">{{ formatDateTime(e.created_utc, true) }}</span>
                        <strong>{{ eventLabel(e.event_type) }}</strong>
                        <span v-if="e.actor_name || e.actor_email">
                          — {{ e.actor_name || e.actor_email }}
                        </span>
                        <div v-if="e.notes" class="event-notes">{{ e.notes }}</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ══════════════════════════ AUDIT ══════════════════════════ -->
    <!-- Every recorded action and who took it. Scoped server-side, so this shows
         exactly what the signed-in user is allowed to see and nothing more. -->
    <!-- ═══════════════════ POLLY INTAKE DIAGNOSTICS ═══════════════════ -->
    <template v-else-if="tab === 'diagnostics'">
      <div class="filter-row">
        <!-- Only the day window re-queries; everything else filters the loaded
             rows, so sifting is instant. -->
        <select v-model="pollyDays" class="filter-select" @change="loadPollyEvents">
          <option :value="1">Last 24 Hours</option>
          <option :value="7">Last 7 Days</option>
          <option :value="14">Last 14 Days</option>
          <option :value="90">Last 90 Days</option>
        </select>

        <button class="refresh-btn" :disabled="pollyLoading" @click="loadPollyEvents">
          {{ pollyLoading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>

      <div class="filter-row">
        <!-- The flag filter first: it is what you actually reach for, because
             each option is a distinct reason the pipeline declined to act. -->
        <select v-model="pollyFilters.flag" class="filter-select">
          <option value="">Any outcome flag</option>
          <option value="no_owner">Not on access list</option>
          <option value="reason_unmatched">Reason unmatched</option>
          <option value="bypassed">Bypassed internal approval</option>
          <option value="emailed">Emails sent</option>
          <option value="clean">No flags</option>
        </select>

        <select v-model="pollyFilters.mode" class="filter-select">
          <option value="">Any mode</option>
          <option value="dry">Dry</option>
          <option value="live">Live</option>
        </select>

        <select v-model="pollyFilters.action" class="filter-select">
          <option value="">Any outcome</option>
          <option v-for="a in pollyFacets.actions" :key="a" :value="a">{{ a }}</option>
        </select>

        <select v-model="pollyFilters.event" class="filter-select">
          <option value="">Any event</option>
          <option v-for="e in pollyFacets.events" :key="e" :value="e">{{ e }}</option>
        </select>

        <select v-if="pollyFacets.divisions.length > 1" v-model="pollyFilters.division" class="filter-select">
          <option value="">Any division</option>
          <option v-for="d in pollyFacets.divisions" :key="d" :value="d">{{ d }}</option>
        </select>

        <select v-if="pollyFacets.tiers.length > 1" v-model="pollyFilters.tier" class="filter-select">
          <option value="">Any tier</option>
          <option v-for="t in pollyFacets.tiers" :key="t" :value="t">{{ t }}</option>
        </select>

        <select v-model="pollyFilters.raisedBy" class="filter-select">
          <option value="">Any loan officer</option>
          <option v-for="r in pollyFacets.raisedBy" :key="r" :value="r">{{ r }}</option>
        </select>

        <select v-if="pollyFacets.pollyUsers.length > 1" v-model="pollyFilters.pollyUser"
                class="filter-select">
          <option value="">Any Polly user</option>
          <option v-for="u in pollyFacets.pollyUsers" :key="u" :value="u">{{ u }}</option>
        </select>

        <input v-model.trim="pollyFilters.loan" class="filter-select audit-search"
               type="text" placeholder="Loan number..." />

        <button v-if="pollyFiltersActive" class="link-btn" @click="clearPollyFilters">
          Clear filters
        </button>
      </div>

      <div class="diag-explainer">
        Every price exception Polly sent us, and what the concession pipeline made of it.
        <strong>Dry</strong> rows were measured only — nothing was filed and nobody was emailed —
        which is what you get while the intake switch is off.
      </div>

      <div v-if="pollyError" class="blocked-card">
        <div class="blocked-icon"><i class="fa-duotone fa-circle-exclamation"></i></div>
        <div>
          <div class="blocked-title">Couldn't load the diagnostics</div>
          <div class="blocked-text">{{ pollyError }}</div>
        </div>
      </div>

      <div class="audit-count">
        <span v-if="pollyLoading">Loading events...</span>
        <span v-else-if="!pollyEvents.length">
          No Polly price-exception events have reached the pipeline yet.
        </span>
        <span v-else-if="pollyFiltersActive">
          Showing {{ filteredPollyEvents.length }} of {{ pollyEvents.length }}
          event{{ pollyEvents.length === 1 ? '' : 's' }}
        </span>
        <span v-else>
          {{ pollyEvents.length }} event{{ pollyEvents.length === 1 ? '' : 's' }},
          {{ pollyDryRunCount }} measured only
        </span>
      </div>

      <div v-if="!pollyLoading && pollyEvents.length && !filteredPollyEvents.length" class="state-card">
        No events match these filters.
        <button class="link-btn" @click="clearPollyFilters">Clear filters</button>
      </div>

      <div v-if="filteredPollyEvents.length" class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Mode</th>
              <th>Event</th>
              <th>Loan</th>
              <th>Raised by (loan officer)</th>
              <th>Tier / Product</th>
              <th class="num">Points</th>
              <th class="num">bps</th>
              <th class="num">Ceiling</th>
              <th>Rule</th>
              <th>Outcome</th>
              <th>Would email</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredPollyEvents" :key="row.id">
              <td class="nowrap">{{ formatDateTime(row.created_utc) }}</td>
              <td>
                <span class="mode-pill" :class="row.dry_run ? 'is-dry' : 'is-live'">
                  {{ row.dry_run ? 'Dry' : 'Live' }}
                </span>
              </td>
              <td class="nowrap">{{ row.polly_event || '—' }}</td>
              <td class="nowrap">{{ row.loan_number || '—' }}</td>
              <!-- Raised by = the loan's OFFICER, matching how a concession filed in
                   Swift is attributed. The Polly user who actually pressed the button
                   is shown beneath when it is someone else. -->
              <td>
                {{ row.requested_by_email || '—' }}
                <!-- No owner means no ceilings and no approvers could be resolved, which is
                     the single most common reason nothing gets filed. -->
                <span v-if="row.requested_by_email && !row.owner_resolved" class="diag-flag is-warn">
                  not on access list
                </span>
                <div v-if="row.division" class="diag-sub">{{ row.division }}</div>
                <div v-if="row.polly_user_email && row.polly_user_email !== row.requested_by_email"
                     class="diag-sub">
                  via {{ row.polly_user_email }}
                </div>
              </td>
              <td>
                {{ row.tier || '—' }}
                <span v-if="row.reason_code && !row.reason_matched" class="diag-flag is-warn">
                  reason unmatched
                </span>
                <div v-if="row.product_type" class="diag-sub">{{ row.product_type }}</div>
              </td>
              <td class="num">{{ row.exception_points != null ? Number(row.exception_points).toFixed(3) : '—' }}</td>
              <td class="num">{{ row.requested_bps != null ? Number(row.requested_bps).toFixed(2) : '—' }}</td>
              <td class="num">{{ row.threshold_bps != null ? Number(row.threshold_bps).toFixed(2) : '—' }}</td>
              <td>
                {{ row.matched_rule || '—' }}
                <div v-if="row.routing" class="diag-sub">{{ row.routing }}</div>
              </td>
              <td>
                <span class="diag-action">{{ row.action || '—' }}</span>
                <div v-if="row.status" class="diag-sub">{{ row.status }}</div>
                <span v-if="row.internal_approval_bypassed" class="diag-flag is-warn">
                  bypassed internal approval
                </span>
              </td>
              <td>
                <div v-if="row.would_email_approvers" class="diag-sub">
                  approvers: {{ row.would_email_approvers }}
                </div>
                <div v-if="row.would_email_branch_manager" class="diag-sub">
                  branch: {{ row.would_email_branch_manager }}
                </div>
                <span v-if="!row.would_email_approvers && !row.would_email_branch_manager">—</span>
                <span v-if="row.emails_sent" class="diag-flag is-sent">sent</span>
              </td>
              <td class="diag-message">{{ row.message || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-else-if="tab === 'audit'">
      <div class="filter-row">
        <select v-model="audit.days" class="filter-select" @change="loadAudit(0)">
          <option :value="7">Last 7 Days</option>
          <option :value="30">Last 30 Days</option>
          <option :value="90">Last 90 Days</option>
          <option :value="365">Last Year</option>
          <option :value="0">All time</option>
        </select>

        <select v-model="audit.event_type" class="filter-select" @change="loadAudit(0)">
          <option value="">All actions</option>
          <option v-for="t in auditPage.event_types" :key="t" :value="t">{{ eventLabel(t) }}</option>
        </select>

        <select v-model="audit.actor_email" class="filter-select" @change="loadAudit(0)">
          <option value="">Anyone</option>
          <option v-for="a in auditPage.actors" :key="a" :value="a">{{ a }}</option>
        </select>

        <select v-if="filterDivisions.length > 1" v-model="audit.division"
                class="filter-select" @change="loadAudit(0)">
          <option value="">{{ context.is_concessions_admin ? 'All divisions' : 'All my divisions' }}</option>
          <option v-for="d in filterDivisions" :key="d" :value="d">{{ d }}</option>
        </select>

        <input v-model.trim="audit.loan_number" class="filter-select audit-search"
               type="text" placeholder="Loan number..." @keyup.enter="loadAudit(0)" />

        <button class="refresh-btn" :disabled="auditLoading" @click="loadAudit(0)">
          {{ auditLoading ? 'Loading...' : 'Apply' }}
        </button>
      </div>

      <div class="audit-count">
        <span v-if="auditLoading">Loading actions...</span>
        <span v-else-if="!auditPage.total">No actions recorded for this filter.</span>
        <span v-else>
          Showing {{ auditPage.entries.length }} of {{ auditPage.total }}
          action{{ auditPage.total === 1 ? '' : 's' }}
        </span>
      </div>

      <div v-if="auditPage.entries.length" class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Who</th>
              <th>Role</th>
              <th>Loan</th>
              <th>Division</th>
              <th>Concession type</th>
              <th class="num">bps</th>
              <th>Change</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in auditPage.entries" :key="e.id">
              <td class="nowrap">{{ formatDateTime(e.created_utc, true) }}</td>
              <td>
                <span class="pill" :class="auditEventClass(e.event_type)">{{ eventLabel(e.event_type) }}</span>
              </td>
              <td class="wrap-email">
                {{ e.actor_name || e.actor_email || 'System' }}
                <div v-if="e.actor_name && e.actor_email" class="sub-note">{{ e.actor_email }}</div>
              </td>
              <td>{{ e.actor_role || '—' }}</td>
              <td class="nowrap">
                {{ e.loan_number || '—' }}
                <div v-if="borrowerName(e) !== '—'" class="sub-note">{{ borrowerName(e) }}</div>
              </td>
              <td>{{ e.division || '—' }}</td>
              <td>{{ e.tier === 'corp' ? 'Corporate' : 'Branch' }}</td>
              <td class="num">{{ formatNumber(e.requested_bps) }}</td>
              <td class="nowrap">
                <span v-if="e.from_status || e.to_status">
                  {{ e.from_status ? statusLabel(e.from_status) : '—' }} &rarr;
                  {{ e.to_status ? statusLabel(e.to_status) : '—' }}
                </span>
                <span v-else>—</span>
              </td>
              <td>
                <div v-if="e.notes" class="audit-notes">{{ e.notes }}</div>
                <div v-if="e.reason_text" class="sub-note">{{ e.reason_text }}</div>
                <div v-if="!e.notes && !e.reason_text" class="sub-note">—</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="auditPage.total > auditPage.take" class="audit-pager">
        <button class="link-btn" :disabled="auditLoading || auditPage.skip <= 0"
                @click="loadAudit(Math.max(0, auditPage.skip - auditPage.take))">
          Newer
        </button>
        <span class="sub-note">
          {{ auditPage.skip + 1 }}–{{ auditPage.skip + auditPage.entries.length }}
          of {{ auditPage.total }}
        </span>
        <button class="link-btn"
                :disabled="auditLoading || (auditPage.skip + auditPage.take) >= auditPage.total"
                @click="loadAudit(auditPage.skip + auditPage.take)">
          Older
        </button>
      </div>
    </template>

    <!-- Decision confirm -->
    <div v-if="decisionRow" class="modal-backdrop" @click.self="closeDecision">
      <div class="modal-card">
        <div class="modal-header">
          <div>
            <h3 class="modal-title">
              {{ decisionVerdict === 'approve' ? 'Approve concession' : 'Reject concession' }}
            </h3>
            <div class="modal-subtitle">
              {{ borrowerName(decisionRow) }}
              <span v-if="decisionRow.loan_number"> — {{ decisionRow.loan_number }}</span>
            </div>
          </div>
          <button class="close-btn" @click="closeDecision">✕</button>
        </div>

        <div class="modal-body">
          <div class="decision-summary">
            <div>{{ formatNumber(decisionRow.requested_bps) }} bps
              <span v-if="decisionRow.requested_amount">({{ formatMoney(decisionRow.requested_amount) }})</span>
              &middot; {{ decisionRow.tier === 'corp' ? 'Corporate' : 'Branch' }}
              &middot; {{ productLabel(decisionRow.product_type) }}
            </div>
            <div class="sub-note">{{ decisionRow.reason_text || decisionRow.reason_code }}</div>
            <div v-if="decisionRow.requester_notes" class="sub-note">{{ decisionRow.requester_notes }}</div>
          </div>

          <div v-if="decisionVerdict === 'approve'" class="decision-note-banner">
            Approving creates the price exception in Polly on the loan.
          </div>

          <label class="form-field full">
            <span class="form-label">Notes</span>
            <textarea v-model.trim="decisionNotes" class="form-input form-textarea" rows="3"
                      placeholder="Optional — recorded on the audit trail"></textarea>
          </label>

          <div v-if="decisionError" class="inline-error">{{ decisionError }}</div>

          <div class="modal-footer-actions">
            <button class="cancel-btn" :disabled="decidingId" @click="closeDecision">Cancel</button>
            <button :class="decisionVerdict === 'approve' ? 'save-btn' : 'reject-confirm-btn'"
                    :disabled="decidingId" @click="confirmDecision">
              {{ decidingId ? 'Working...' : (decisionVerdict === 'approve' ? 'Approve' : 'Reject') }}
            </button>
          </div>
        </div>
      </div>
    </div>
      <div v-if="showApproverModal" class="modal-backdrop" @click.self="closeApproverImport">
        <div class="modal-card approver-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Concession Approvers</h3>
              <div class="modal-subtitle">
                Upload the "Polly User Access Level" workbook to refresh the roster
              </div>
            </div>
            <button class="close-btn" @click="closeApproverImport">✕</button>
          </div>

          <div class="modal-body approver-modal-body">
            <!-- current roster summary -->
            <div class="approver-current">
              <span v-if="approverRosterLoading">Loading the current roster...</span>
              <template v-else>
                <strong>{{ approverRosterCount }}</strong> active
                {{ approverRosterCount === 1 ? 'user' : 'users' }} on the roster
                <span v-if="approverLastImport" class="approver-current-sub">
                  — last import {{ formatDateTime(approverLastImport.committed_utc || approverLastImport.created_utc, true) }}
                  by {{ approverLastImport.uploaded_by_name || approverLastImport.uploaded_by_email || 'unknown' }}
                </span>
                <span v-else class="approver-current-sub">— never imported</span>
              </template>
            </div>

            <!-- Reason list. Polly owns this one, so it is a refresh, not an
                 edit surface. Lives here because this is the admin surface. -->
            <div class="approver-current">
              <span v-if="reasonsLoading">Loading the reason list...</span>
              <template v-else>
                <strong>{{ reasonCount }}</strong> active concession
                {{ reasonCount === 1 ? 'reason' : 'reasons' }}
                <span v-if="reasonLastSynced" class="approver-current-sub">
                  — last synced {{ formatDateTime(reasonLastSynced, true) }}
                </span>
                <span v-else class="approver-current-sub">— not synced yet</span>
                <button class="approver-toggle"
                        :disabled="reasonSyncing"
                        @click="syncConcessionReasons">
                  {{ reasonSyncing ? 'Refreshing...' : 'Refresh now' }}
                </button>
              </template>

              <div class="approver-table-note">
                Polly owns this list — it syncs on its own in the background. Refresh only if you
                changed something in Polly and want it reflected immediately.
              </div>

              <div v-if="reasonSyncMessage" class="approver-table-note">{{ reasonSyncMessage }}</div>

              <!-- A Polly type we could not map to branch/corp means that reason
                   is offered for BOTH tiers. Worth saying out loud. -->
              <div v-if="reasonUnmappedTypes.length" class="approver-table-note">
                Unrecognized Polly types: <strong>{{ reasonUnmappedTypes.join(', ') }}</strong>.
                Reasons with those types are offered for both branch and corp requests.
              </div>

              <div v-if="reasonSyncError" class="inline-error">{{ reasonSyncError }}</div>
            </div>

            <!-- file picker -->
            <div class="approver-drop"
                 :class="{ 'is-over': approverDragOver, 'has-file': !!approverFile }"
                 @dragover.prevent="approverDragOver = true"
                 @dragleave.prevent="approverDragOver = false"
                 @drop.prevent="onApproverDrop">
              <input ref="approverFileInput"
                     type="file"
                     accept=".xlsx,.xlsm"
                     class="approver-file-input"
                     @change="onApproverFilePicked" />

              <template v-if="approverFile">
                <i class="fa-duotone fa-file-excel approver-drop-icon"></i>
                <div class="approver-drop-title">{{ approverFile.name }}</div>
                <div class="approver-drop-hint">{{ formatFileSize(approverFile.size) }}</div>
              </template>
              <template v-else>
                <i class="fa-duotone fa-cloud-arrow-up approver-drop-icon"></i>
                <div class="approver-drop-title">Drop the .xlsx here</div>
                <div class="approver-drop-hint">or click to choose a file</div>
              </template>

              <button class="approver-browse-btn"
                      :disabled="approverBusy"
                      @click="$refs.approverFileInput.click()">
                {{ approverFile ? 'Choose a different file' : 'Choose file' }}
              </button>
            </div>

            <div v-if="approverError" class="inline-error">{{ approverError }}</div>

            <div v-if="approverBusy" class="approver-busy">
              <i class="fa-duotone fa-spinner fa-spin"></i>
              {{ approverCommitting ? 'Applying the import...' : 'Reading the workbook...' }}
            </div>

            <!-- preview -->
            <template v-if="approverPreview && !approverBusy">
              <div class="approver-result-banner"
                   :class="approverPreview.committed ? 'is-committed' : 'is-preview'">
                <i :class="approverPreview.committed ? 'fa-duotone fa-circle-check' : 'fa-duotone fa-eye'"></i>
                <span v-if="approverPreview.committed">
                  Import applied. The roster is now live.
                </span>
                <span v-else>
                  Preview only — nothing has been saved yet.
                </span>
              </div>

              <div class="approver-counts">
                <div class="approver-count-card">
                  <div class="approver-count-value">{{ approverPreview.rows_read }}</div>
                  <div class="approver-count-label">Rows read</div>
                </div>
                <div class="approver-count-card is-new">
                  <div class="approver-count-value">{{ approverPreview.rows_new }}</div>
                  <div class="approver-count-label">New</div>
                </div>
                <div class="approver-count-card is-changed">
                  <div class="approver-count-value">{{ approverPreview.rows_changed }}</div>
                  <div class="approver-count-label">Updated</div>
                </div>
                <div class="approver-count-card is-off">
                  <div class="approver-count-value">{{ approverPreview.rows_deactivating }}</div>
                  <div class="approver-count-label">Deactivated</div>
                </div>
                <div class="approver-count-card">
                  <div class="approver-count-value">{{ approverPreview.rows_unchanged }}</div>
                  <div class="approver-count-label">Unchanged</div>
                </div>
                <div class="approver-count-card" v-if="approverPreview.rows_skipped">
                  <div class="approver-count-value">{{ approverPreview.rows_skipped }}</div>
                  <div class="approver-count-label">Skipped</div>
                </div>
              </div>

              <!-- deactivation is the destructive part; call it out -->
              <div v-if="approverPreview.rows_deactivating > 0 && !approverPreview.committed"
                   class="approver-warn-banner">
                <i class="fa-duotone fa-triangle-exclamation"></i>
                <span>
                  {{ approverPreview.rows_deactivating }}
                  {{ approverPreview.rows_deactivating === 1 ? 'user is' : 'users are' }}
                  missing from this file and will be switched off. They are never deleted — a later
                  upload that includes them turns them back on.
                </span>
              </div>

              <!-- per-division rollup -->
              <div class="approver-section-title">By division</div>
              <div class="approver-table-wrap">
                <table class="approver-table">
                  <thead>
                    <tr>
                      <th>Division</th>
                      <th class="num">Users</th>
                      <th class="num">Branch approvers</th>
                      <th class="num">Corp approvers</th>
                      <th class="num">Ceilings set</th>
                      <th>Can request</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in approverPreview.divisions" :key="d.division">
                      <td>{{ d.division }}</td>
                      <td class="num">{{ d.user_count }}</td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.branch_approver_count === 0 }">
                        {{ d.branch_approver_count }}
                      </td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.corp_approver_count === 0 }">
                        {{ d.corp_approver_count }}
                      </td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.threshold_cells_populated === 0 }">
                        {{ d.threshold_cells_populated }}
                      </td>
                      <td>
                        <span class="approver-pill" :class="d.can_request ? 'is-yes' : 'is-no'">
                          {{ d.can_request ? 'Yes' : 'Restricted' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="approver-table-note">
                A division with users but no approvers has nobody to send an over-threshold request to,
                so those land in this queue as concession requests. Zero ceilings set means every
                concession there auto-approves.
              </div>

              <!-- warnings -->
              <div v-if="approverPreview.warnings_total > 0">
                <div class="approver-section-title">
                  Warnings ({{ approverPreview.warnings_total }})
                  <button class="approver-toggle" @click="showApproverWarnings = !showApproverWarnings">
                    {{ showApproverWarnings ? 'Hide' : 'Show' }}
                  </button>
                </div>
                <div v-if="showApproverWarnings" class="approver-warn-list">
                  <div v-for="(w, i) in approverPreview.warnings" :key="i" class="approver-warn-row">
                    <span class="approver-warn-row-loc">Row {{ w.row }}</span>
                    <span class="approver-warn-row-who">{{ w.email || w.field }}</span>
                    <span class="approver-warn-row-msg">{{ w.message }}</span>
                  </div>
                  <div v-if="approverPreview.warnings_truncated" class="approver-warn-more">
                    Showing the first {{ approverPreview.warnings.length }} of
                    {{ approverPreview.warnings_total }} warnings.
                  </div>
                </div>
                <div class="approver-table-note">
                  Warnings never block an import — they flag cells worth a second look in the workbook.
                </div>
              </div>

              <!-- change detail -->
              <div v-if="approverPreview.changes && approverPreview.changes.length">
                <div class="approver-section-title">
                  What changes
                  <button class="approver-toggle" @click="showApproverChanges = !showApproverChanges">
                    {{ showApproverChanges ? 'Hide' : 'Show' }}
                  </button>
                </div>
                <div v-if="showApproverChanges" class="approver-change-list">
                  <div v-for="(c, i) in approverPreview.changes" :key="i" class="approver-change-row">
                    <span class="approver-change-tag" :class="'tag-' + c.change_type">
                      {{ changeTypeLabel(c.change_type) }}
                    </span>
                    <span class="approver-change-who">
                      {{ c.name || c.email }}
                      <span class="approver-change-email" v-if="c.name">{{ c.email }}</span>
                    </span>
                    <span class="approver-change-div">{{ c.division || '—' }}</span>
                    <span class="approver-change-fields">
                      <span v-for="(f, fi) in c.fields" :key="fi" class="approver-change-field">
                        {{ f.field }}: <em>{{ f.from || 'empty' }}</em> → <strong>{{ f.to || 'empty' }}</strong>
                      </span>
                    </span>
                  </div>
                  <div v-if="approverPreview.changes_truncated" class="approver-warn-more">
                    Showing the first {{ approverPreview.changes.length }} rows. The counts above are complete.
                  </div>
                </div>
              </div>
            </template>

            <div class="modal-footer-actions">
              <button class="cancel-btn" :disabled="approverBusy" @click="closeApproverImport">
                {{ approverPreview && approverPreview.committed ? 'Done' : 'Cancel' }}
              </button>
              <button v-if="approverPreview && !approverPreview.committed"
                      class="save-btn"
                      :disabled="approverBusy || !approverFile"
                      @click="commitApproverImport">
                Apply import
              </button>
              <button v-else-if="!approverPreview"
                      class="save-btn"
                      :disabled="approverBusy || !approverFile"
                      @click="previewApproverImport">
                Preview changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════ APPROVER ROSTER (read only) ══════════════════ -->
      <!-- What the importer has already loaded, so it can be reviewed without
           uploading anything. Deliberately has no edit controls: the workbook is
           the source of truth, and the only way to change the roster is to import
           a new one. -->
      <div v-if="showRosterModal" class="modal-backdrop" @click.self="closeApproverRoster">
        <div class="modal-card roster-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Concession Approvers</h3>
              <div class="modal-subtitle">
                The current roster, as loaded from the "Polly User Access Level" workbook
              </div>
            </div>
            <button class="close-btn" @click="closeApproverRoster">✕</button>
          </div>

          <div class="modal-body approver-modal-body">
            <div class="roster-controls">
              <div class="roster-search-wrap">
                <i class="fa-duotone fa-magnifying-glass roster-search-icon"></i>
                <input v-model.trim="rosterSearch"
                       type="search"
                       class="form-input roster-search"
                       placeholder="Search name, email, job title, division, branch, or approver" />
              </div>

              <label class="roster-check">
                <input type="checkbox"
                       :checked="rosterIncludeInactive"
                       :disabled="rosterLoading"
                       @change="toggleRosterInactive($event)" />
                <span>Include inactive</span>
              </label>

              <button class="approver-toggle"
                      :disabled="rosterLoading"
                      @click="loadApproverRoster">
                {{ rosterLoading ? 'Loading...' : 'Refresh' }}
              </button>
            </div>

            <div v-if="rosterError" class="inline-error">{{ rosterError }}</div>

            <div v-if="rosterLoading" class="approver-busy">
              <i class="fa-duotone fa-spinner fa-spin"></i> Loading the roster...
            </div>

            <template v-else>
              <div class="approver-current">
                <strong>{{ filteredRoster.length }}</strong>
                {{ filteredRoster.length === 1 ? 'user' : 'users' }}
                <span v-if="rosterSearch" class="approver-current-sub">
                  matching "{{ rosterSearch }}" — of {{ rosterRows.length }} on the roster
                </span>
                <span v-else class="approver-current-sub">
                  on the roster{{ rosterIncludeInactive ? ', including inactive' : ' (active only)' }}
                </span>
              </div>

              <div v-if="!rosterRows.length" class="approver-table-note">
                Nothing has been imported yet. Use the import button to load the workbook.
              </div>

              <div v-else-if="!filteredRoster.length" class="approver-table-note">
                No one on the roster matches that search.
              </div>

              <div v-else class="approver-table-wrap">
                <table class="approver-table">
                  <thead>
                    <tr>
                      <th class="roster-expand-col"></th>
                      <th class="sortable" @click="sortRosterBy('name')">
                        Name <span class="sort-caret">{{ rosterSortCaret('name') }}</span>
                      </th>
                      <th class="sortable" @click="sortRosterBy('email')">
                        Email <span class="sort-caret">{{ rosterSortCaret('email') }}</span>
                      </th>
                      <th class="sortable" @click="sortRosterBy('job_title')">
                        Job title <span class="sort-caret">{{ rosterSortCaret('job_title') }}</span>
                      </th>
                      <th class="sortable" @click="sortRosterBy('division')">
                        Division <span class="sort-caret">{{ rosterSortCaret('division') }}</span>
                      </th>
                      <th class="sortable" @click="sortRosterBy('branch')">
                        Branch <span class="sort-caret">{{ rosterSortCaret('branch') }}</span>
                      </th>
                      <th class="sortable" @click="sortRosterBy('can_request')">
                        Can request <span class="sort-caret">{{ rosterSortCaret('can_request') }}</span>
                      </th>
                      <th class="num sortable" @click="sortRosterBy('approvers')">
                        Approvers <span class="sort-caret">{{ rosterSortCaret('approvers') }}</span>
                      </th>
                      <th class="num sortable" @click="sortRosterBy('ceilings')">
                        Ceilings <span class="sort-caret">{{ rosterSortCaret('ceilings') }}</span>
                      </th>
                      <th v-if="rosterIncludeInactive" class="sortable" @click="sortRosterBy('is_active')">
                        Status <span class="sort-caret">{{ rosterSortCaret('is_active') }}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="row in filteredRoster" :key="row.id">
                      <tr class="roster-row"
                          :class="{ 'is-open': rosterExpandedId === row.id, 'is-inactive': !row.is_active }"
                          @click="toggleRosterRow(row.id)">
                        <td class="roster-expand-col">
                          <i class="fa-duotone"
                             :class="rosterExpandedId === row.id ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                        </td>
                        <td class="nowrap">{{ rosterName(row) }}</td>
                        <td>{{ row.email }}</td>
                        <td>{{ row.job_title || '—' }}</td>
                        <td>{{ row.division || '—' }}</td>
                        <td>{{ rosterBranch(row) }}</td>
                        <td>
                          <span class="approver-pill" :class="row.can_request ? 'is-yes' : 'is-no'">
                            {{ row.can_request ? 'Yes' : 'Restricted' }}
                          </span>
                        </td>
                        <!-- Zero approvers on someone who may request means an
                             over-threshold request has nowhere to go. -->
                        <td class="num"
                            :class="{ 'is-zero': row.can_request && rosterApproverCount(row) === 0 }">
                          {{ rosterApproverCount(row) }}
                        </td>
                        <td class="num">{{ rosterCeilingCount(row) }}</td>
                        <td v-if="rosterIncludeInactive">
                          <span class="approver-pill" :class="row.is_active ? 'is-yes' : 'is-no'">
                            {{ row.is_active ? 'Active' : 'Inactive' }}
                          </span>
                        </td>
                      </tr>

                      <tr v-if="rosterExpandedId === row.id" :key="row.id + '-detail'" class="roster-detail-row">
                        <td :colspan="rosterIncludeInactive ? 10 : 9">
                          <div class="roster-detail">
                            <div class="roster-detail-block">
                              <div class="approver-section-title">Approves for this user</div>
                              <div class="roster-kv">
                                <span class="roster-kv-key">Branch</span>
                                <span class="roster-kv-val">{{ rosterApprovers(row, 'branch') }}</span>
                              </div>
                              <div class="roster-kv">
                                <span class="roster-kv-key">Corp</span>
                                <span class="roster-kv-val">{{ rosterApprovers(row, 'corp') }}</span>
                              </div>
                              <div class="roster-kv">
                                <span class="roster-kv-key">Alert threshold</span>
                                <span class="roster-kv-val">
                                  {{ row.forecast_deposit_alert_threshold === null
                                     || row.forecast_deposit_alert_threshold === undefined
                                      ? '—'
                                      : row.forecast_deposit_alert_threshold + ' bps' }}
                                </span>
                              </div>
                            </div>

                            <div class="roster-detail-block roster-detail-wide">
                              <div class="approver-section-title">Auto-approve ceilings</div>
                              <div class="approver-table-wrap">
                                <table class="approver-table roster-ceiling-table">
                                  <thead>
                                    <tr>
                                      <th>Product</th>
                                      <th class="num">Branch bps</th>
                                      <th class="num">Branch max $</th>
                                      <th class="num">Corp bps</th>
                                      <th class="num">Corp max $</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="c in rosterCeilings(row)" :key="c.key">
                                      <td>{{ c.label }}</td>
                                      <td class="num">{{ rosterBps(c.branchBps) }}</td>
                                      <td class="num">{{ rosterAmount(c.branchMax) }}</td>
                                      <td class="num">{{ rosterBps(c.corpBps) }}</td>
                                      <td class="num">{{ rosterAmount(c.corpMax) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <!-- Worth stating outright: an empty cell is not a zero
                                   ceiling, it is no ceiling at all. -->
                              <div class="approver-table-note">
                                A dash means no ceiling is configured, which the decision engine
                                treats as auto-approve — not as a limit of zero.
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>

              <div v-if="filteredRoster.length" class="approver-table-note">
                Read only. The workbook is the source of truth — import a new one to change the roster.
              </div>
            </template>

            <div class="modal-footer-actions">
              <button class="cancel-btn" @click="closeApproverRoster">Close</button>
            </div>
          </div>
        </div>
      </div>

  </div>
</template>

<script>
import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import api from '@/api/api.service'
  import auth from '@/authConfig.js'

  export default {
    components: { PageHeaderPortal },
    name: 'Concessions',
    data() {
      return {
        tab: 'request',
        contextLoading: true,
        loading: false,
        days: 90,
        statusFilter: '',
        divisionFilter: '',
        // Client-side. Set by the pipeline's "view them" link so a loan's whole
        // concession history is one click from the loan itself.
        loanFilter: '',

        // ── the locked-loan pipeline the request form is driven from ──
        // Selecting a row here is what prefills the form, so the LO only has to
        // supply what the loan cannot tell us.
        pipeline: {
          loans: [],
          scope: 'mine',
          available_scopes: ['mine'],
          total: 0,
          truncated: false,
          note: ''
        },
        pipelineScope: 'mine',
        pipelineSearch: '',
        // Sorting and filtering are client-side because the whole set comes down
        // in one call. Default matches the server's own ORDER BY, so the first
        // paint is not silently re-ordered under the user.
        pipelineSort: { field: 'lock_expires', dir: 'asc' },
        pipelineFilters: {
          expiry: '',
          concessions: '',
          product: '',
          milestone: '',
          officer: ''
        },
        pipelineLoading: false,
        pipelineError: '',

        context: {
          email: '',
          found: false,
          can_request: false,
          blocked_reason: '',
          roster_note: '',
          division: '',
          branch_code: '',
          branch_name: '',
          is_approver: false,
          approver_divisions: [],
          is_concessions_admin: false,
          all_divisions: [],
          branch_reasons: [],
          corp_reasons: [],
          products: []
        },

        // Polly intake diagnostics (admin tab).
        pollyEvents: [],
        pollyDays: 14,
        pollyLoading: false,
        pollyError: '',
        // Filtered client-side: the day window already bounds the result set, so
        // sifting is instant and does not re-hit the API on every change.
        pollyFilters: {
          mode: '',
          event: '',
          action: '',
          division: '',
          tier: '',
          raisedBy: '',
          pollyUser: '',
          loan: '',
          flag: ''
        },

        // Concession approver roster importer (managers only). The roster is
        // maintained in the "Polly User Access Level" workbook, not in this app.
        showApproverModal: false,
        approverFile: null,
        approverDragOver: false,
        approverPreview: null,   // the server's diff report, preview or applied
        approverError: '',
        approverBusy: false,
        approverCommitting: false,
        approverRosterCount: 0,
        approverRosterLoading: false,

        // ── Read-only roster viewer (separate from the importer above) ──
        // The whole roster comes down in one call, so search and expansion are
        // client-side — same pattern as the locked-loan pipeline on this page.
        showRosterModal: false,
        rosterRows: [],
        rosterLoading: false,
        rosterError: '',
        rosterSearch: '',
        // Inactive users are never deleted, only switched off by an import that
        // omitted them, so reviewing "who dropped off" is a real question.
        rosterIncludeInactive: false,
        rosterExpandedId: '',
        // Division first, matching how the workbook itself is organised, so the
        // opening order is the one a reviewer already has in their head.
        rosterSort: { field: 'division', dir: 'asc' },
        approverLastImport: null,
        showApproverWarnings: false,
        showApproverChanges: false,
        // Concession reason list — mirrored from Polly's price-exception reasons.
        reasonCount: 0,
        reasonLastSynced: null,
        reasonsLoading: false,
        reasonSyncing: false,
        reasonSyncMessage: '',
        reasonSyncError: '',
        reasonUnmappedTypes: [],

        requests: [],
        expandedId: null,
        events: [],
        eventsLoading: false,

        // Audit feed. Filters live in `audit`; the server returns the page plus
        // the distinct actions/actors present, so the dropdowns show what is
        // actually there rather than a hardcoded list.
        audit: {
          days: 90,
          event_type: '',
          actor_email: '',
          division: '',
          loan_number: ''
        },
        auditPage: { entries: [], total: 0, skip: 0, take: 100, event_types: [], actors: [] },
        auditLoading: false,

        decisionRow: null,
        decisionVerdict: '',
        decisionNotes: '',
        decisionError: '',
        decidingId: null
      }
    },
    computed: {
      // Every term must match somewhere on the row, so "conv michigan" narrows
      // rather than widening the way a single OR'd string would. Approver emails
      // are in the haystack on purpose: "who does Dana approve for" is the
      // question this screen gets asked most.
      filteredRoster() {
        const terms = this.rosterSearch.toLowerCase().split(/\s+/).filter(Boolean)

        const rows = !terms.length ? this.rosterRows : this.rosterRows.filter(row => {
          const haystack = [
            row.email, row.first_name, row.last_name, row.job_title,
            row.division, row.branch_code, row.branch_name,
            row.branch_approver_1_email, row.branch_approver_2_email, row.branch_approver_3_email,
            row.corp_approver_1_email, row.corp_approver_2_email, row.corp_approver_3_email
          ].filter(Boolean).join(' ').toLowerCase()

          return terms.every(t => haystack.includes(t))
        })

        const { field, dir } = this.rosterSort
        const sign = dir === 'desc' ? -1 : 1

        // Copied before sorting: sort() mutates, and rosterRows is what the
        // expanded row is matched against by id.
        return rows.slice().sort((a, b) => {
          const av = this.rosterSortValue(a, field)
          const bv = this.rosterSortValue(b, field)

          // Missing values sort last in BOTH directions — same rule the pipeline
          // table uses. Reversing the sort should not parade the blanks to the top.
          if (av === null && bv === null) return this.rosterTieBreak(a, b)
          if (av === null) return 1
          if (bv === null) return -1

          const cmp = typeof av === 'string'
            ? av.localeCompare(bv)
            : (av < bv ? -1 : av > bv ? 1 : 0)

          return cmp !== 0 ? cmp * sign : this.rosterTieBreak(a, b)
        })
      },

      // Options come from the rows actually loaded, so a filter can never offer a
      // value that would return nothing.
      pipelineFacets() {
        const distinct = (fn) => [...new Set(
          this.pipeline.loans.map(fn).filter(v => v !== null && v !== undefined && v !== '')
        )].sort()

        // Product needs both halves: the code we filter on and the label shown.
        // An unrecognized Encompass product has no code, so it gets its own
        // bucket rather than vanishing from the list.
        const products = []
        const seen = new Set()
        for (const loan of this.pipeline.loans) {
          const value = loan.product_type || '__unmapped'
          if (seen.has(value)) continue
          seen.add(value)
          products.push({
            value,
            label: loan.product_type ? this.productLabel(loan.product_type) : 'Unmapped product'
          })
        }
        products.sort((a, b) => a.label.localeCompare(b.label))

        return {
          products,
          milestones: distinct(l => l.current_milestone),
          officers: distinct(l => l.loan_officer_name || l.loan_officer_email)
        }
      },
      // The grid is not always "yours" — an admin looking at every locked loan in
      // the company should not be told we are loading theirs.
      pipelineScopeNoun() {
        switch (this.pipelineScope) {
          case 'branch': return "your branch's locked loans"
          case 'all': return 'all locked loans'
          default: return 'your locked loans'
        }
      },
      manualRequestUrl() {
        return this.$router.resolve({ name: 'concessionRequest' }).href
      },
      pipelineFiltersActive() {
        const f = this.pipelineFilters
        return !!(f.expiry || f.concessions || f.product || f.milestone || f.officer)
      },
      visiblePipelineLoans() {
        const f = this.pipelineFilters
        const rows = this.pipeline.loans.filter(loan => {
          if (f.product) {
            const value = loan.product_type || '__unmapped'
            if (value !== f.product) return false
          }
          if (f.milestone && loan.current_milestone !== f.milestone) return false
          if (f.officer && (loan.loan_officer_name || loan.loan_officer_email) !== f.officer) {
            return false
          }

          if (f.expiry) {
            const days = this.lockDaysLeft(loan)
            if (f.expiry === 'none') {
              if (days !== null) return false
            } else if (f.expiry === 'expired') {
              if (days === null || days >= 0) return false
            } else {
              // A numeric window means "still live and due within N days" —
              // an already-expired lock is not something expiring soon.
              const within = Number(f.expiry)
              if (days === null || days < 0 || days > within) return false
            }
          }

          if (f.concessions) {
            const onFile = Number(loan.concession_on_file_amount) || 0
            if (f.concessions === 'none' && onFile > 0) return false
            if (f.concessions === 'some' && onFile <= 0) return false
            if (f.concessions === 'pending' && !loan.swift_pending_count) return false
          }

          return true
        })

        const { field, dir } = this.pipelineSort
        const sign = dir === 'desc' ? -1 : 1

        // Copied before sorting: sort() mutates, and pipeline.loans is the
        // source of truth the selection is re-pointed against.
        return rows.slice().sort((a, b) => {
          const av = this.pipelineSortValue(a, field)
          const bv = this.pipelineSortValue(b, field)

          // Missing values sort last in BOTH directions. Flipping the direction
          // should not parade the blanks to the top.
          if (av === null && bv === null) return 0
          if (av === null) return 1
          if (bv === null) return -1

          if (typeof av === 'string') return av.localeCompare(bv) * sign
          return (av < bv ? -1 : av > bv ? 1 : 0) * sign
        })
      },
      // Loan filtering is client-side, so everything the dashboard reports has to
      // read off the filtered list — a "Total" of 40 above a table showing 1 loan
      // would be answering a question nobody asked.
      visibleRequests() {
        const loan = (this.loanFilter || '').trim().toLowerCase()
        if (!loan) return this.requests
        return this.requests.filter(r =>
          String(r.loan_number || '').toLowerCase().includes(loan))
      },
      pollyDryRunCount() {
        return this.pollyEvents.filter(x => x.dry_run).length
      },
      // Options come from the rows actually loaded, so a filter can never offer
      // a value that would return nothing.
      pollyFacets() {
        const distinct = (key) => [...new Set(
          this.pollyEvents.map(r => r[key]).filter(v => v !== null && v !== undefined && v !== '')
        )].sort()

        return {
          events: distinct('polly_event'),
          actions: distinct('action'),
          divisions: distinct('division'),
          tiers: distinct('tier'),
          raisedBy: distinct('requested_by_email'),
          pollyUsers: distinct('polly_user_email')
        }
      },
      filteredPollyEvents() {
        const f = this.pollyFilters
        const loan = (f.loan || '').trim().toLowerCase()

        return this.pollyEvents.filter(r => {
          if (f.mode === 'dry' && !r.dry_run) return false
          if (f.mode === 'live' && r.dry_run) return false
          if (f.event && r.polly_event !== f.event) return false
          if (f.action && r.action !== f.action) return false
          if (f.division && r.division !== f.division) return false
          if (f.tier && r.tier !== f.tier) return false
          if (f.raisedBy && r.requested_by_email !== f.raisedBy) return false
          if (f.pollyUser && r.polly_user_email !== f.pollyUser) return false

          if (loan && !String(r.loan_number || '').toLowerCase().includes(loan)) return false

          // The flags are the reason this tab exists — each one is a distinct
          // way the pipeline declined to do the obvious thing.
          switch (f.flag) {
            case 'no_owner':
              return !r.owner_resolved
            case 'reason_unmatched':
              return !!r.reason_code && !r.reason_matched
            case 'bypassed':
              return !!r.internal_approval_bypassed
            case 'emailed':
              return !!r.emails_sent
            case 'clean':
              return r.owner_resolved &&
                     !(r.reason_code && !r.reason_matched) &&
                     !r.internal_approval_bypassed
            default:
              return true
          }
        })
      },
      pollyFiltersActive() {
        const f = this.pollyFilters
        return !!(f.mode || f.event || f.action || f.division || f.tier ||
                  f.raisedBy || f.pollyUser || f.loan || f.flag)
      },
      // An admin filters across every division; everyone else only sees the
      // ones they approve for. Server-side scoping is what actually enforces
      // this — the list here just stops the UI offering a filter that would
      // come back empty.
      filterDivisions() {
        return this.context.is_concessions_admin
          ? (this.context.all_divisions || [])
          : (this.context.approver_divisions || [])
      },
      pendingForMe() {
        return this.requests.filter(r => r.status === 'pending')
      },
      // Approved here but Polly hasn't taken it. Called out because the price
      // exception does not exist on the loan until Polly accepts.
      pollyProblems() {
        return this.visibleRequests.filter(r =>
          (r.polly_sync_status === 'failed' || r.polly_sync_status === 'pending') &&
          (r.status === 'approved' || r.status === 'auto_approved'))
      },
      avgBps() {
        const vals = this.visibleRequests
          .map(r => Number(r.requested_bps))
          .filter(v => !Number.isNaN(v) && v > 0)
        if (!vals.length) return '—'
        return this.formatNumber(vals.reduce((a, b) => a + b, 0) / vals.length)
      },
      totalDollars() {
        const total = this.visibleRequests
          .filter(r => r.status === 'approved' || r.status === 'auto_approved')
          .reduce((sum, r) => sum + (Number(r.requested_amount) || 0), 0)
        return total > 0 ? this.formatMoney(total) : '—'
      }
    },
    async mounted() {
      await this.loadContext()
      // Approvers land on the queue they're expected to act on.
      if (this.context.is_approver) {
        this.tab = 'dashboard'
        await this.loadRequests()
      } else if (!this.context.can_request) {
        this.tab = 'dashboard'
        await this.loadRequests()
      } else {
        await this.loadPipeline()
      }
    },
    // Vue 3: beforeDestroy never runs, so the search debounce has to be torn
    // down here or a keystroke can fire a request after the page is gone.
    beforeUnmount() {
      if (this._pipelineDebounce) clearTimeout(this._pipelineDebounce)
    },
    methods: {
      async reload() {
        await this.loadContext()
        if (this.tab === 'dashboard') await this.loadRequests()
        else if (this.tab === 'request') await this.loadPipeline()
      },

      // ── the locked-loan pipeline ─────────────────────────────────────────
      async switchToRequest() {
        this.tab = 'request'
        if (!this.pipeline.loans.length && !this.pipelineLoading) {
          await this.loadPipeline()
        }
      },
      // Numbers and dates default to descending (biggest / soonest first is what
      // people reach for); text defaults to A-Z.
      sortPipelineBy(field) {
        if (this.pipelineSort.field === field) {
          this.pipelineSort = { field, dir: this.pipelineSort.dir === 'asc' ? 'desc' : 'asc' }
          return
        }
        const numeric = ['loan_amount', 'interest_rate', 'concession_on_file_amount']
        this.pipelineSort = { field, dir: numeric.includes(field) ? 'desc' : 'asc' }
      },
      sortCaret(field) {
        if (this.pipelineSort.field !== field) return ''
        return this.pipelineSort.dir === 'asc' ? '\u25B2' : '\u25BC'
      },
      // Null means "no value" and is what sorts last — distinct from 0, which is
      // a real answer for a concession total.
      pipelineSortValue(loan, field) {
        switch (field) {
          case 'borrower': {
            const name = [loan.borrower_last, loan.borrower_first].filter(Boolean).join(' ')
            return name ? name.toLowerCase() : null
          }
          case 'loan_officer_name': {
            const who = loan.loan_officer_name || loan.loan_officer_email
            return who ? who.toLowerCase() : null
          }
          case 'product': {
            // Sort by what is on screen, so the order matches what is read.
            const label = loan.product_type
              ? this.productLabel(loan.product_type)
              : (loan.product_type_raw || '')
            return label ? label.toLowerCase() : null
          }
          case 'lock_expires': {
            if (!loan.lock_expires) return null
            const t = new Date(loan.lock_expires).getTime()
            return Number.isNaN(t) ? null : t
          }
          case 'loan_amount':
          case 'interest_rate':
          case 'concession_on_file_amount': {
            const n = Number(loan[field])
            return loan[field] === null || loan[field] === undefined || Number.isNaN(n) ? null : n
          }
          default: {
            const v = loan[field]
            return v === null || v === undefined || v === '' ? null : String(v).toLowerCase()
          }
        }
      },
      // A different scope brings a different set of officers and products, so
      // carrying filters across would empty the grid for no visible reason.
      async onPipelineScopeChange() {
        this.clearPipelineFilters()
        await this.loadPipeline()
      },
      // Row click mirrors the Request anchor. window.open rather than a router push:
      // the whole point is a separate tab, and the grid stays put behind it.
      openRequest(loan) {
        window.open(this.requestUrl(loan), '_blank', 'noopener')
      },
      clearPipelineFilters() {
        this.pipelineFilters = {
          expiry: '', concessions: '', product: '', milestone: '', officer: ''
        }
      },
      // The request lives at its own URL so it can open in a new browser tab and
      // survive a reload. Built with the router so a base path change cannot strand it.
      requestUrl(loan) {
        return this.$router.resolve({
          name: 'concessionRequest',
          params: { loanId: loan.loan_id }
        }).href
      },
      scopeLabel(scope) {
        switch (scope) {
          case 'mine': return 'My loans'
          case 'branch': return 'My branch'
          case 'all': return 'All locked loans'
          default: return scope || ''
        }
      },
      // Searched server-side, so a typed loan number finds a loan that was never
      // on screen. Debounced because that is a real query against Reporting.
      onPipelineSearch() {
        if (this._pipelineDebounce) clearTimeout(this._pipelineDebounce)
        this._pipelineDebounce = setTimeout(() => this.loadPipeline(), 350)
      },
      async loadPipeline() {
        if (this._pipelineDebounce) {
          clearTimeout(this._pipelineDebounce)
          this._pipelineDebounce = null
        }

        this.pipelineLoading = true
        this.pipelineError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionLoanPipeline(
            this.pipelineScope, this.pipelineSearch, 1000, tokenResponse.accessToken)
          const body = res?.data || {}

          if (body.issuccess === false || body.isSuccess === false) {
            this.pipeline = { ...this.pipeline, loans: [], total: 0, truncated: false, note: '' }
            this.pipelineError = body.message || `Could not load ${this.pipelineScopeNoun}.`
            return
          }

          const data = body.data || {}
          this.pipeline = {
            loans: Array.isArray(data.loans) ? data.loans : [],
            scope: data.scope || this.pipelineScope,
            available_scopes: (data.available_scopes && data.available_scopes.length)
              ? data.available_scopes
              : ['mine'],
            total: data.total || 0,
            truncated: !!data.truncated,
            note: data.note || ''
          }

          // The server decides the scope — asking for one you don't hold is
          // served as your own loans. Follow what it actually applied, or the
          // dropdown and the column set would describe rows that aren't there.
          this.pipelineScope = this.pipeline.scope

        } catch (e) {
          this.pipeline = { ...this.pipeline, loans: [], total: 0, truncated: false, note: '' }
          this.pipelineError = this.serverMessage(e, `Could not load ${this.pipelineScopeNoun}.`)
        } finally {
          this.pipelineLoading = false
        }
      },
      // Jumps to this loan's concession history. Widens the window first — the
      // point is the loan's whole story, not the last 90 days of it.
      async viewLoanConcessions(loan) {
        this.loanFilter = loan.loan_number || ''
        this.statusFilter = ''
        this.days = 365
        this.tab = 'dashboard'
        await this.loadRequests()
      },
      // Days until the lock expires, or null when there is no readable date.
      lockDaysLeft(loan) {
        if (!loan || !loan.lock_expires) return null
        const expires = new Date(loan.lock_expires)
        if (Number.isNaN(expires.getTime())) return null

        const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
        const days = (startOfDay(expires) - startOfDay(new Date())) / 86400000
        return Math.round(days)
      },
      lockExpiryLabel(loan) {
        const days = this.lockDaysLeft(loan)
        if (days === null) return ''
        if (days < 0) return `expired ${Math.abs(days)}d ago`
        if (days === 0) return 'expires today'
        return `${days}d left`
      },
      isLockUrgent(loan) {
        const days = this.lockDaysLeft(loan)
        return days !== null && days <= 7
      },
      async switchToAudit() {
        this.tab = 'audit'
        if (!this.auditPage.entries.length) await this.loadAudit(0)
      },
      // ── Concession approver roster, read only ────────────────────────────
      // Reviewing what is already loaded. No edit path by design: the workbook is
      // the source of truth and the importer is the only way to change it.
      openApproverRoster() {
        this.showRosterModal = true
        this.rosterExpandedId = ''
        this.loadApproverRoster()
      },
      closeApproverRoster() {
        this.showRosterModal = false
        this.rosterSearch = ''
        this.rosterExpandedId = ''
        this.rosterError = ''
      },
      toggleRosterInactive(event) {
        this.rosterIncludeInactive = !!event.target.checked
        // activeOnly is a server-side filter, so the scope change needs a refetch
        // rather than a client-side filter over what we already hold.
        this.loadApproverRoster()
      },
      toggleRosterRow(id) {
        this.rosterExpandedId = this.rosterExpandedId === id ? '' : id
      },
      async loadApproverRoster() {
        this.rosterLoading = true
        this.rosterError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const response = await api.GetConcessionApprovers(
            !this.rosterIncludeInactive,
            tokenResponse.accessToken
          )

          const data = response?.data?.data || response?.data || []

          // Stored in whatever order the server sent. Ordering is filteredRoster's
          // job now that the headers are sortable — doing it in both places would
          // mean a refresh could silently re-order under the chosen sort.
          this.rosterRows = Array.isArray(data) ? data : []
        } catch (error) {
          this.rosterRows = []
          this.rosterError =
            error?.response?.data?.message ||
            error?.message ||
            'The approver roster could not be loaded.'
        } finally {
          this.rosterLoading = false
        }
      },
      // Every column opens ascending, including the two counts. The pipeline table
      // opens numerics descending, but the interesting end of "approvers" and
      // "ceilings" is ZERO — nobody to route to, and nothing capped — so the
      // useful click here is the one that brings those to the top.
      sortRosterBy(field) {
        this.rosterSort = this.rosterSort.field === field
          ? { field, dir: this.rosterSort.dir === 'asc' ? 'desc' : 'asc' }
          : { field, dir: 'asc' }
      },
      rosterSortCaret(field) {
        if (this.rosterSort.field !== field) return ''
        return this.rosterSort.dir === 'asc' ? '▲' : '▼'
      },
      // Keeps equal groups — a whole division, everyone with no ceilings — reading
      // alphabetically instead of in whatever order the server happened to send.
      // Always ascending, so it does not flip with the column direction.
      rosterTieBreak(a, b) {
        return (a.last_name || '').localeCompare(b.last_name || '') ||
               (a.first_name || '').localeCompare(b.first_name || '') ||
               (a.email || '').localeCompare(b.email || '')
      },
      // null means "no value" and sorts last. Distinct from 0, which is a real
      // answer for both counts and the thing worth finding.
      rosterSortValue(row, field) {
        switch (field) {
          case 'name': {
            const name = [row.last_name, row.first_name].filter(Boolean).join(' ')
            return name ? name.toLowerCase() : null
          }
          case 'email':
          case 'job_title':
          case 'division':
            return row[field] ? String(row[field]).toLowerCase() : null
          case 'branch': {
            // Sort by what is on screen, so the order matches what is read.
            const label = this.rosterBranch(row)
            return label === '—' ? null : label.toLowerCase()
          }
          case 'can_request':
            return row.can_request ? 1 : 0
          case 'is_active':
            return row.is_active ? 1 : 0
          case 'approvers':
            return this.rosterApproverCount(row)
          case 'ceilings':
            return this.rosterCeilingCount(row)
          default:
            return null
        }
      },
      rosterName(row) {
        return `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—'
      },
      rosterBranch(row) {
        const code = (row.branch_code || '').trim()
        const name = (row.branch_name || '').trim()

        if (!code) return name || '—'
        if (!name) return code

        // The workbook's branch name usually ALREADY ends in the code —
        // "Salt Lake City, UT (UTSL01)" — so prefixing it printed the code twice
        // and cost the table a column's worth of width for nothing. Where the two
        // genuinely differ (code MIAA00, name "Dallas, TX 4 (TXDA04)") both are
        // still shown, because that mismatch is worth seeing.
        if (name.toLowerCase().includes(code.toLowerCase())) return name

        return `${code} — ${name}`
      },
      rosterApproverCount(row) {
        return [
          row.branch_approver_1_email, row.branch_approver_2_email, row.branch_approver_3_email,
          row.corp_approver_1_email, row.corp_approver_2_email, row.corp_approver_3_email
        ].filter(e => !!e && String(e).trim()).length
      },
      rosterApprovers(row, tier) {
        const emails = [
          row[`${tier}_approver_1_email`],
          row[`${tier}_approver_2_email`],
          row[`${tier}_approver_3_email`]
        ].filter(e => !!e && String(e).trim())

        return emails.length ? emails.join(', ') : 'Nobody configured'
      },
      // The six products the workbook carries ceilings for, in its own order.
      rosterCeilings(row) {
        return [
          { key: 'conv', label: 'Conventional' },
          { key: 'fha', label: 'FHA' },
          { key: 'usda', label: 'USDA' },
          { key: 'va', label: 'VA' },
          { key: 'jumbo', label: 'Jumbo' },
          { key: 'nonqm', label: 'Non-QM' }
        ].map(p => ({
          key: p.key,
          label: p.label,
          branchBps: row[`auto_approve_branch_${p.key}_bps`],
          branchMax: row[`auto_approve_branch_${p.key}_max_amount`],
          corpBps: row[`auto_approve_corp_${p.key}_bps`],
          corpMax: row[`auto_approve_corp_${p.key}_max_amount`]
        }))
      },
      // How many of the 24 ceiling cells this user actually has set. 0 means every
      // concession they request auto-approves.
      rosterCeilingCount(row) {
        return this.rosterCeilings(row).reduce((total, c) => total +
          [c.branchBps, c.branchMax, c.corpBps, c.corpMax]
            .filter(v => v !== null && v !== undefined).length, 0)
      },
      rosterBps(value) {
        return value === null || value === undefined ? '—' : `${value}`
      },
      rosterAmount(value) {
        return value === null || value === undefined ? '—' : this.formatMoney(value)
      },

      // ── Concession approvers importer ────────────────────────────────────
      // The roster lives in the "Polly User Access Level" workbook. A manager
      // uploads it here; the server parses, diffs, and only writes on commit.
      openApproverImport() {
        this.showApproverModal = true
        this.resetApproverImport()
        this.loadApproverRosterSummary()
        this.loadConcessionReasonsSummary()
      },
      closeApproverImport() {
        if (this.approverBusy) return
        this.showApproverModal = false
        this.resetApproverImport()
      },
      resetApproverImport() {
        this.approverFile = null
        this.approverPreview = null
        this.approverError = ''
        this.approverDragOver = false
        this.approverCommitting = false
        this.showApproverWarnings = false
        this.showApproverChanges = false
        if (this.$refs.approverFileInput) this.$refs.approverFileInput.value = ''
      },
      async loadApproverRosterSummary() {
        this.approverRosterLoading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const [rosterRes, importRes] = await Promise.allSettled([
            api.GetConcessionApprovers(true, token),
            api.GetConcessionApproverImports(1, token)
          ])

          if (rosterRes.status === 'fulfilled') {
            const data = rosterRes.value?.data?.data || rosterRes.value?.data || []
            this.approverRosterCount = Array.isArray(data) ? data.length : 0
          }

          if (importRes.status === 'fulfilled') {
            const data = importRes.value?.data?.data || importRes.value?.data || []
            this.approverLastImport = Array.isArray(data) && data.length ? data[0] : null
          }
        } catch {
          // A failed summary is cosmetic — the upload itself still works.
        } finally {
          this.approverRosterLoading = false
        }
      },
      onApproverFilePicked(event) {
        const file = event?.target?.files?.[0]
        if (file) this.setApproverFile(file)
      },
      onApproverDrop(event) {
        this.approverDragOver = false
        const file = event?.dataTransfer?.files?.[0]
        if (file) this.setApproverFile(file)
      },
      setApproverFile(file) {
        const name = (file.name || '').toLowerCase()
        if (!name.endsWith('.xlsx') && !name.endsWith('.xlsm')) {
          this.approverError = 'That is not an Excel workbook. Pick the .xlsx export from Polly.'
          return
        }

        this.approverFile = file
        // A new file invalidates any preview on screen — otherwise a manager
        // could confirm one file's numbers and apply a different file.
        this.approverPreview = null
        this.approverError = ''
        this.showApproverWarnings = false
        this.showApproverChanges = false
      },
      previewApproverImport() {
        return this._runApproverImport(false)
      },
      commitApproverImport() {
        return this._runApproverImport(true)
      },
      async _runApproverImport(commit) {
        if (!this.approverFile || this.approverBusy) return

        this.approverBusy = true
        this.approverCommitting = commit
        this.approverError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const res = await api.ImportConcessionApprovers(this.approverFile, commit, token)

          const body = res?.data || {}
          const report = body.data || body

          // The API lowercases every PascalCase property (LowerCaseNamingPolicy
          // in Startup.cs), so the ServiceResponse flag arrives as `issuccess`.
          // Our own DTO fields carry [JsonPropertyName] and keep snake_case.
          const succeeded = body.issuccess !== undefined ? body.issuccess : body.isSuccess

          if (succeeded === false) {
            this.approverError = body.message || 'The import failed.'
            // Keep whatever the server managed to report, so counts and
            // warnings are still visible alongside the error.
            if (report && typeof report === 'object') this.approverPreview = report
            return
          }

          this.approverPreview = report

          if (commit) {
            // Refresh the summary line so the roster count reflects the import.
            this.loadApproverRosterSummary()
          } else if (report?.warnings_total > 0) {
            this.showApproverWarnings = true
          }
        } catch (e) {
          // A parse failure or a refused empty file comes back as a 400, so the
          // useful text is on the error response, not the success path.
          const serverMessage = e?.response?.data?.message
            || e?.response?.data?.error
          this.approverError = serverMessage
            || 'Could not reach the server to run the import. Nothing was changed.'
        } finally {
          this.approverBusy = false
          this.approverCommitting = false
        }
      },
      async loadConcessionReasonsSummary() {
        this.reasonsLoading = true
        this.reasonSyncError = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionReasons(null, tokenResponse.accessToken)
          const data = res?.data?.data || []
          this.applyReasonSummary(Array.isArray(data) ? data : [])
        } catch {
          // Cosmetic — the sync button still works if this read failed.
        } finally {
          this.reasonsLoading = false
        }
      },
      applyReasonSummary(reasons) {
        this.reasonCount = reasons.length
        // Newest sync stamp across the list. Nulls are omitted by the API, so
        // filter before comparing.
        const stamps = reasons
          .map(r => r.polly_synced_utc)
          .filter(Boolean)
          .sort()
        this.reasonLastSynced = stamps.length ? stamps[stamps.length - 1] : null
      },
      async syncConcessionReasons() {
        if (this.reasonSyncing) return

        this.reasonSyncing = true
        this.reasonSyncError = ''
        this.reasonSyncMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.SyncConcessionReasonsFromPolly(tokenResponse.accessToken)
          const body = res?.data || {}
          const result = body.data || {}

          this.reasonSyncMessage = body.message || 'Reasons synced.'
          this.reasonUnmappedTypes = Array.isArray(result.unmapped_types) ? result.unmapped_types : []
          this.applyReasonSummary(Array.isArray(result.reasons) ? result.reasons : [])
        } catch (e) {
          this.reasonSyncError = e?.response?.data?.message
            || 'Could not reach Polly to refresh the reason list. The existing reasons are unchanged.'
        } finally {
          this.reasonSyncing = false
        }
      },
      changeTypeLabel(changeType) {
        switch (changeType) {
          case 'new': return 'New'
          case 'changed': return 'Updated'
          case 'deactivating': return 'Deactivating'
          case 'reactivating': return 'Reactivating'
          default: return changeType || ''
        }
      },
      formatFileSize(bytes) {
        const size = Number(bytes)
        if (!size || Number.isNaN(size)) return ''
        if (size < 1024) return `${size} B`
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`
        return `${(size / (1024 * 1024)).toFixed(1)} MB`
      },
      async switchToDiagnostics() {
        this.tab = 'diagnostics'
        if (!this.pollyEvents.length) await this.loadPollyEvents()
      },
      clearPollyFilters() {
        this.pollyFilters = {
          mode: '', event: '', action: '', division: '',
          tier: '', raisedBy: '', pollyUser: '', loan: '', flag: ''
        }
      },
      async loadPollyEvents() {
        this.pollyLoading = true
        this.pollyError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionPollyEventLog(this.pollyDays, tokenResponse.accessToken)
          const body = res?.data || {}

          // The endpoint refuses non-admins server-side; surface that rather than
          // showing an empty table as though nothing had happened.
          if (body.issuccess === false || body.isSuccess === false) {
            this.pollyEvents = []
            this.pollyError = body.message || 'Could not load the Polly intake diagnostics.'
            return
          }

          this.pollyEvents = body.data || []
        } catch (e) {
          this.pollyEvents = []
          this.pollyError =
            e?.response?.data?.message ||
            e?.message ||
            'Could not load the Polly intake diagnostics.'
        } finally {
          this.pollyLoading = false
        }
      },
      async loadAudit(skip) {
        this.auditLoading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionAuditTrail({
            days: this.audit.days,
            event_type: this.audit.event_type || null,
            actor_email: this.audit.actor_email || null,
            division: this.audit.division || null,
            loan_number: this.audit.loan_number || null,
            skip: skip || 0,
            take: this.auditPage.take || 100
          }, tokenResponse.accessToken)

          const page = res?.data?.data
          if (page) {
            this.auditPage = {
              entries: page.entries || [],
              total: page.total || 0,
              skip: page.skip || 0,
              take: page.take || 100,
              // Keep the previous facets when a filter narrows things to
              // nothing, otherwise the dropdowns empty out and the user is
              // stuck unable to widen the filter again.
              event_types: (page.event_types && page.event_types.length)
                ? page.event_types
                : this.auditPage.event_types,
              actors: (page.actors && page.actors.length)
                ? page.actors
                : this.auditPage.actors
            }
          }
        } catch {
          this.auditPage = { ...this.auditPage, entries: [], total: 0 }
        } finally {
          this.auditLoading = false
        }
      },
      auditEventClass(type) {
        if (type === 'approved' || type === 'auto_approved' ||
            type === 'polly_price_exception_created') return 'is-approved'
        if (type === 'rejected' || type === 'polly_push_failed') return 'is-rejected'
        if (type === 'revenue_alert_created') return 'is-warn'
        if (type === 'created' || type === 'routed') return 'is-pending'
        return 'is-neutral'
      },
      async switchToDashboard() {
        this.tab = 'dashboard'
        if (!this.requests.length) await this.loadRequests()
      },
      async loadContext() {
        this.contextLoading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionUserContext(tokenResponse.accessToken)
          const data = res?.data?.data
          if (data) {
            this.context = {
              ...this.context,
              ...data,
              approver_divisions: data.approver_divisions || [],
              all_divisions: data.all_divisions || [],
              branch_reasons: data.branch_reasons || [],
              corp_reasons: data.corp_reasons || [],
              products: data.products || []
            }
          }
        } catch (e) {
          this.pipelineError = this.serverMessage(e, 'Could not load your concession access.')
        } finally {
          this.contextLoading = false
        }
      },
      async loadRequests() {
        this.loading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionRequests(
            this.days, this.statusFilter, this.divisionFilter, tokenResponse.accessToken)
          const data = res?.data?.data || []
          this.requests = Array.isArray(data) ? data : []
        } catch (e) {
          this.requests = []
        } finally {
          this.loading = false
        }
      },
      openDecision(row, verdict) {
        this.decisionRow = row
        this.decisionVerdict = verdict
        this.decisionNotes = ''
        this.decisionError = ''
      },
      closeDecision() {
        if (this.decidingId) return
        this.decisionRow = null
        this.decisionVerdict = ''
        this.decisionNotes = ''
        this.decisionError = ''
      },
      async confirmDecision() {
        if (!this.decisionRow || this.decidingId) return

        this.decidingId = this.decisionRow.id
        this.decisionError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.DecideConcessionRequest({
            id: this.decisionRow.id,
            decision: this.decisionVerdict,
            decision_notes: this.decisionNotes || null
          }, tokenResponse.accessToken)

          const result = res?.data?.data
          // Polly may have refused even though the decision landed. Surface it
          // rather than closing on a green note.
          if (result?.polly_error) {
            this.decisionError = `Decision recorded, but Polly hasn't accepted it: ${result.polly_error}`
            await this.loadRequests()
            return
          }

          this.decisionRow = null
          this.decisionVerdict = ''
          await this.loadRequests()
        } catch (e) {
          this.decisionError = this.serverMessage(e, 'Could not record the decision.')
        } finally {
          this.decidingId = null
        }
      },
      async toggleDetail(row) {
        if (this.expandedId === row.id) {
          this.expandedId = null
          return
        }

        this.expandedId = row.id
        this.events = []
        this.eventsLoading = true

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionRequestEvents(row.id, tokenResponse.accessToken)
          const data = res?.data?.data || []
          this.events = Array.isArray(data) ? data : []
        } catch {
          this.events = []
        } finally {
          this.eventsLoading = false
        }
      },

      // ── display helpers ──────────────────────────────────────────────────
      // The API lowercases PascalCase properties, so ServiceResponse.Message
      // arrives as `message`, and failures come back as a 400.
      serverMessage(error, fallback) {
        return error?.response?.data?.message || fallback
      },
      countBy(status) {
        return this.visibleRequests.filter(r => r.status === status).length
      },
      borrowerName(r) {
        const name = [r?.borrower_first, r?.borrower_last].filter(Boolean).join(' ')
        return name || '—'
      },
      productLabel(code) {
        switch (code) {
          case 'conv': return 'Conventional'
          case 'fha': return 'FHA'
          case 'usda': return 'USDA'
          case 'va': return 'VA'
          case 'jumbo': return 'Jumbo'
          case 'nonqm': return 'Non QM'
          default: return code || '—'
        }
      },
      statusLabel(status) {
        switch (status) {
          case 'pending': return 'Pending'
          case 'auto_approved': return 'Auto-approved'
          case 'approved': return 'Approved'
          case 'rejected': return 'Rejected'
          case 'cancelled': return 'Cancelled'
          case 'expired': return 'Expired'
          default: return status || '—'
        }
      },
      statusClass(status) {
        if (status === 'approved' || status === 'auto_approved') return 'is-approved'
        if (status === 'rejected') return 'is-rejected'
        if (status === 'pending') return 'is-pending'
        return 'is-neutral'
      },
      pollyLabel(state) {
        switch (state) {
          case 'sent': return 'In Polly'
          case 'pending': return 'Not sent'
          case 'failed': return 'Failed'
          case 'not_required': return 'N/A'
          default: return '—'
        }
      },
      pollyClass(state) {
        if (state === 'sent') return 'is-approved'
        if (state === 'failed') return 'is-rejected'
        if (state === 'pending') return 'is-pending'
        return 'is-neutral'
      },
      routingLabel(routing) {
        switch (routing) {
          case 'auto': return 'Auto-approved, nobody notified'
          case 'approvers': return 'Sent to the configured approvers'
          case 'lock_desk': return 'Lock desk (no approvers configured for the division)'
          default: return routing || '—'
        }
      },
      eventLabel(type) {
        switch (type) {
          case 'created': return 'Filed'
          case 'auto_approved': return 'Auto-approved'
          case 'routed': return 'Routed'
          case 'approved': return 'Approved'
          case 'rejected': return 'Rejected'
          case 'polly_price_exception_created': return 'Created in Polly'
          case 'polly_push_failed': return 'Polly push failed'
          case 'branch_manager_notified': return 'Branch manager alerted'
          case 'revenue_alert_created': return 'Revenue alert raised'
          case 'revenue_alert_checked': return 'Revenue check run'
          default: return (type || '').replace(/_/g, ' ')
        }
      },
      formatDateTime(value, withTime = false) {
        if (!value) return '—'
        const str = typeof value === 'string' && !value.endsWith('Z') && !value.includes('+')
          ? value + 'Z'
          : value
        const date = new Date(str)
        if (Number.isNaN(date.getTime())) return '—'
        if (withTime) {
          return date.toLocaleDateString('en-US') + ' ' +
            date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        }
        return date.toLocaleDateString('en-US')
      },
      // Loan dates out of Reporting are plain calendar dates, NOT UTC stamps.
      // formatDateTime bolts a Z onto anything without one, which would slide a
      // midnight lock expiry back to the previous day.
      formatLoanDate(value) {
        if (!value) return '—'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return '—'
        return date.toLocaleDateString('en-US')
      },
      // "0 bps" when there is genuinely nothing on the file, "—" when we cannot work
      // it out (no loan amount to divide by). Those are different answers.
      bpsLabel(bps, amount) {
        if (bps !== null && bps !== undefined && !Number.isNaN(Number(bps))) {
          return `${this.formatNumber(bps)} bps`
        }
        return Number(amount) ? '—' : '0 bps'
      },
      // The server sends the pending total in dollars; the column speaks bps.
      pendingBps(loan) {
        const amount = Number(loan.swift_pending_amount) || 0
        const base = Number(loan.total_loan_amount) || Number(loan.loan_amount) || 0
        if (amount <= 0 || base <= 0) return null
        return (amount / base) * 10000
      },
      formatRate(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toFixed(3) + '%'
      },
      formatNumber(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
      },
      formatMoney(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toLocaleString('en-US', {
          style: 'currency', currency: 'USD', maximumFractionDigits: 0
        })
      }
    }
  }
</script>

<style scoped>
  .concessions-page {
    display: flex;
    flex-direction: column;
    padding: 20px 24px 32px;
    background: #f7f9fc;
    min-height: 100%;
    font-family: 'Segoe UI', Tahoma, sans-serif;
    color: #22293a;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }
  .page-header-left { display: flex; align-items: center; gap: 12px; }
  .page-icon {
    height: 44px;
    width: 44px;
    border-radius: 8px;
    background: #123a71;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 19px;
  }
  .page-title { font-size: 21px; font-weight: 600; margin: 0; }
  .page-subtitle { font-size: 12.5px; color: #7b8397; margin: 2px 0 0; }
  .header-actions { display: flex; align-items: center; gap: 8px; }

  .filter-select, .refresh-btn {
    height: 38px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #3c4658;
    font-size: 13px;
    padding: 0 10px;
    cursor: pointer;
  }
  .refresh-btn:hover:not(:disabled) { background: #eef4ff; border-color: #c2d4f0; }
  .refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .tab-bar { display: flex; gap: 6px; margin-bottom: 16px; }
  .tab-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid #d9dfeb;
    border-radius: 6px;
    background: #ffffff;
    color: #7b8397;
    cursor: pointer;
  }
  .tab-btn.is-active { background: #123a71; border-color: #123a71; color: #ffffff; }
  .tab-badge {
    background: #a4472f;
    color: #ffffff;
    border-radius: 10px;
    font-size: 10.5px;
    padding: 1px 6px;
  }
  .tab-btn.is-active .tab-badge { background: #ffffff; color: #123a71; }

  .state-card, .request-card, .blocked-card {
    background: #ffffff;
    border: 1px solid #e4e9f2;
    border-radius: 8px;
    padding: 18px;
    font-size: 13px;
    color: #3c4658;
  }

  .blocked-card { display: flex; gap: 14px; align-items: flex-start; }
  .blocked-icon { font-size: 22px; color: #a4472f; }
  .blocked-title { font-size: 15px; font-weight: 600; margin-bottom: 3px; }
  .blocked-text { color: #7b8397; line-height: 1.5; }

  .roster-note-card {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background: #eef4ff;
    border: 1px solid #c2d4f0;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 14px;
    font-size: 12.5px;
    line-height: 1.5;
    color: #123a71;
  }
  .roster-note-icon { font-size: 16px; }
  .roster-note-text { flex: 1; }


  /* ── locked-loan pipeline ─────────────────────────────────────────────── */
  /* The whole row is the control, so it has to read as one. */
  .pipeline-row { cursor: pointer; }
  .pipeline-row:hover > td { background: #f6f8fc; }
  .pipeline-row.is-selected > td { background: #eef4ff; }
  .pipeline-row.is-selected > td:first-child { box-shadow: inset 3px 0 0 #2f6fd0; }

  /* Nothing conceded yet is an answer, just a quiet one — it must not read as
     loudly as a real figure sitting next to it. */
  .conc-zero { color: #9aa2b4; }

  .data-table th.sortable { cursor: pointer; user-select: none; }
  .data-table th.sortable:hover { color: #1c2433; }
  .sort-caret { font-size: 9px; color: #2f6fd0; margin-left: 2px; }






  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 14px;
  }
  .form-field { display: flex; flex-direction: column; gap: 5px; }
  .form-field.full { margin-top: 14px; }
  .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .req { color: #a4472f; }
  .form-input {
    height: 36px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    padding: 0 10px;
    font-size: 13px;
    color: #22293a;
    background: #ffffff;
    font-family: inherit;
  }
  .form-input:focus { outline: none; border-color: #123a71; }
  .form-textarea { height: auto; padding: 8px 10px; resize: vertical; }
  .form-hint { font-size: 11px; color: #7b8397; }
  .form-hint.is-warn { color: #a4472f; }


  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
  }
  .cancel-btn, .save-btn, .reject-confirm-btn {
    height: 36px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    cursor: pointer;
    border: 1px solid #d9dfeb;
    background: #ffffff;
    color: #3c4658;
  }
  .save-btn { background: #123a71; border-color: #123a71; color: #ffffff; }
  .preview-btn {
    height: 36px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    cursor: pointer;
    background: #eef4ff;
    border: 1px solid #c2d4f0;
    color: #123a71;
  }
  .preview-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .reject-confirm-btn { background: #a4472f; border-color: #a4472f; color: #ffffff; }
  .cancel-btn:disabled, .save-btn:disabled, .reject-confirm-btn:disabled {
    opacity: 0.5; cursor: not-allowed;
  }

  .inline-error {
    font-size: 12.5px;
    color: #a4472f;
    background: #fdeeea;
    border: 1px solid #f2c9bd;
    border-radius: 5px;
    padding: 9px 11px;
    margin-top: 14px;
  }


  .summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .summary-card {
    background: #ffffff;
    border: 1px solid #e4e9f2;
    border-radius: 7px;
    padding: 12px 10px;
    text-align: center;
  }
  .summary-value { font-size: 21px; font-weight: 600; }
  .summary-label {
    font-size: 10.5px;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-top: 2px;
  }
  .summary-card.is-pending .summary-value { color: #123a71; }
  .summary-card.is-approved .summary-value { color: #1e7d4f; }
  .summary-card.is-rejected .summary-value { color: #a4472f; }
  .summary-card.is-warn .summary-value { color: #7a5b12; }

  .filter-row {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
    align-items: center;
  }
  /* Eight controls on one row need to stay readable rather than each stretching. */
  .filter-row .filter-select { max-width: 220px; }

  /* The pipeline's two filter rows read as one control strip, so everything on them
     shares a height and a width basis. Without this the selects size to their
     content and the row looks ragged. */
  .pipeline-filters > select,
  .pipeline-filters > input,
  .pipeline-filters > button,
  .pipeline-filters > a {
    height: 38px;
    box-sizing: border-box;
    flex: 0 0 auto;
  }
  .pipeline-filters > select.filter-select {
    flex: 1 1 180px;
    min-width: 180px;
    max-width: 240px;
  }
  /* Search carries the longest placeholder on the page and there is room for it,
     so it gets the slack rather than being clipped mid-word. */
  .pipeline-filters > input.audit-search {
    flex: 2 1 320px;
    min-width: 300px;
    max-width: none;
  }
  /* Buttons match the inputs instead of staying pill-sized next to them. The manual
     link is an <a> so middle-click opens a tab, but it has to read as a button. */
  .pipeline-filters > button,
  .pipeline-filters > a {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 5px;
    border: 1px solid #d9dfeb;
    background: #ffffff;
    color: #3c4658;
    margin-right: 0;
    cursor: pointer;
    white-space: nowrap;
  }
  .pipeline-filters > button:hover:not(:disabled),
  .pipeline-filters > a:hover {
    background: #eef4ff;
    border-color: #c2d4f0;
  }

  .warn-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    line-height: 1.5;
    background: #fff8e6;
    border: 1px solid #f0dca8;
    color: #7a5b12;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }

  .table-wrap {
    background: #ffffff;
    border: 1px solid #e4e9f2;
    border-radius: 8px;
    overflow-x: auto;
  }
  .data-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .data-table th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    background: #f6f8fc;
    padding: 9px 10px;
    border-bottom: 1px solid #e4e9f2;
    white-space: nowrap;
  }
  .data-table td {
    padding: 9px 10px;
    border-bottom: 1px solid #f0f3f8;
    color: #3c4658;
    vertical-align: top;
  }
  .data-table th.num, .data-table td.num { text-align: right; }
  .data-table .nowrap { white-space: nowrap; }
  .data-table .wrap-email { overflow-wrap: anywhere; }
  .sub-note { font-size: 11px; color: #7b8397; margin-top: 2px; }
  /* A lock about to expire, or a concession already in flight, both change what
     you do next — so they must not read as ordinary grey sub-text. */
  .sub-note.is-warn { color: #7a5b12; font-weight: 600; }

  .pill {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 10px;
    padding: 2px 8px;
    white-space: nowrap;
  }
  .pill.is-approved { background: #e9f7ef; color: #1e7d4f; }
  .pill.is-rejected { background: #fdeeea; color: #a4472f; }
  .pill.is-pending { background: #eef4ff; color: #123a71; }
  .pill.is-neutral { background: #f1f2f5; color: #7b8397; }
  .pill.is-warn { background: #fff8e6; color: #7a5b12; }

  .audit-search { min-width: 160px; cursor: text; }
  .audit-count { font-size: 12.5px; color: #7b8397; margin-bottom: 10px; }
  .audit-notes { font-size: 12px; color: #3c4658; line-height: 1.5; max-width: 420px; }
  .audit-pager {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
  }

  .actions-cell { white-space: nowrap; }
  a.approve-btn { display: inline-block; text-decoration: none; }
  .link-btn, .approve-btn, .reject-btn {
    font-size: 11px;
    font-weight: 600;
    border-radius: 4px;
    padding: 3px 9px;
    cursor: pointer;
    margin-right: 4px;
    border: 1px solid #d9dfeb;
    background: #ffffff;
    color: #3c4658;
  }
  .approve-btn { background: #f0fbf5; border-color: #b8e5cc; color: #1b7a47; }
  .reject-btn { background: #fdeeea; border-color: #f2c9bd; color: #a4472f; }
  .link-btn:disabled, .approve-btn:disabled, .reject-btn:disabled {
    opacity: 0.5; cursor: not-allowed;
  }

  .detail-row td { background: #fbfcfe; }
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 18px;
    padding: 6px 2px;
  }
  .detail-title {
    font-size: 10.5px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 5px;
  }
  .detail-line { font-size: 12px; color: #3c4658; line-height: 1.6; overflow-wrap: anywhere; }
  .detail-line.is-warn { color: #a4472f; }
  .detail-line code {
    background: #f1f2f5;
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 11px;
  }
  .event-when { color: #7b8397; margin-right: 6px; }
  .event-notes { color: #7b8397; padding-left: 10px; }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(18, 24, 38, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }
  .modal-card {
    background: #ffffff;
    border-radius: 9px;
    width: 100%;
    max-width: 520px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid #e4e9f2;
  }
  .modal-title { font-size: 16px; font-weight: 600; margin: 0; }
  .modal-subtitle { font-size: 12.5px; color: #7b8397; margin-top: 2px; }
  .close-btn {
    border: none;
    background: none;
    font-size: 15px;
    color: #7b8397;
    cursor: pointer;
    line-height: 1;
  }
  .modal-body { padding: 16px 18px 18px; overflow-y: auto; }
  .modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
  }
  .decision-summary {
    font-size: 13px;
    background: #f6f8fc;
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }
  .decision-note-banner {
    font-size: 12.5px;
    background: #eef4ff;
    border: 1px solid #c2d4f0;
    color: #123a71;
    border-radius: 6px;
    padding: 9px 11px;
    margin-bottom: 12px;
  }

  /* ── Polly intake diagnostics ──────────────────────────────────────────── */
  .diag-explainer {
    font-size: 12.5px;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 9px 11px;
    margin-bottom: 10px;
  }
  .mode-pill {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 10px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
  }
  .mode-pill.is-dry {
    background: #fff7ed;
    color: #9a3412;
    border: 1px solid #fed7aa;
  }
  .mode-pill.is-live {
    background: #f0fbf5;
    color: #1b7a47;
    border: 1px solid #b8e5cc;
  }
  .diag-sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
    word-break: break-word;
  }
  .diag-action {
    font-size: 11.5px;
    font-weight: 700;
    color: #1e293b;
  }
  .diag-flag {
    display: inline-block;
    margin-top: 3px;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
  }
  .diag-flag.is-warn {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  }
  .diag-flag.is-sent {
    background: #eef4ff;
    color: #123a71;
    border: 1px solid #c2d4f0;
  }
  .diag-message {
    font-size: 11.5px;
    color: #475569;
    max-width: 320px;
  }

  /* ── Concession approvers importer ──────────────────────────────────── */
  .approvers-btn {
    height: 38px;
    width: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #7b8397;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }
  .approvers-btn:hover { background: #eef4ff; border-color: #c2d4f0; color: #123a71; }
  .approvers-btn .fa-duotone,
  .approvers-btn .fa-duotone::before,
  .approvers-btn .fa-duotone::after {
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 1;
  }

  .approver-modal-card { max-width: 1080px; width: 96vw; }
  .approver-modal-body { max-height: 78vh; overflow-y: auto; }

  .approver-current {
    font-size: 13px;
    color: #3c4658;
    background: #f6f8fc;
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
  }
  .approver-current-sub { color: #7b8397; }

  .approver-drop {
    position: relative;
    border: 2px dashed #d9dfeb;
    border-radius: 8px;
    background: #fbfcfe;
    padding: 22px 16px 18px;
    text-align: center;
    transition: all 0.15s ease;
  }
  .approver-drop.is-over { border-color: #123a71; background: #eef4ff; }
  .approver-drop.has-file { border-style: solid; border-color: #c2d4f0; background: #ffffff; }
  .approver-file-input { display: none; }
  .approver-drop-icon { font-size: 26px; color: #7b8397; margin-bottom: 8px; display: block; }
  .approver-drop.has-file .approver-drop-icon { color: #1e7d4f; }
  .approver-drop-title { font-size: 14px; font-weight: 600; color: #22293a; }
  .approver-drop-hint { font-size: 12px; color: #7b8397; margin-top: 2px; }
  .approver-browse-btn {
    margin-top: 12px;
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #3c4658;
    cursor: pointer;
  }
  .approver-browse-btn:hover:not(:disabled) { background: #eef4ff; border-color: #c2d4f0; }
  .approver-browse-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .approver-busy {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #3c4658;
    padding: 14px 0 4px;
  }

  .approver-result-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 14px 0 12px;
  }
  .approver-result-banner.is-preview { background: #eef4ff; border: 1px solid #c2d4f0; color: #123a71; }
  .approver-result-banner.is-committed { background: #e9f7ef; border: 1px solid #b9e3cb; color: #1e7d4f; }

  .approver-warn-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    line-height: 1.5;
    background: #fff8e6;
    border: 1px solid #f0dca8;
    color: #7a5b12;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
  }

  .approver-counts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .approver-count-card {
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    background: #ffffff;
    padding: 10px 8px;
    text-align: center;
  }
  .approver-count-value { font-size: 20px; font-weight: 600; color: #22293a; }
  .approver-count-label {
    font-size: 10.5px;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-top: 2px;
  }
  .approver-count-card.is-new .approver-count-value { color: #1e7d4f; }
  .approver-count-card.is-changed .approver-count-value { color: #123a71; }
  .approver-count-card.is-off .approver-count-value { color: #a4472f; }

  .approver-section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 16px 0 8px;
  }
  .approver-toggle {
    border: none;
    background: none;
    color: #123a71;
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
    padding: 0;
  }
  .approver-toggle:hover { text-decoration: underline; }

  /* ── Read-only roster viewer ── */
  /* Wider than the importer's 1080px: this table carries nine columns including
     full email addresses, and at 1080 the last one was cut off the right edge.
     The modal takes the viewport it needs rather than making a reviewer scroll
     sideways to find out whether someone has any ceilings set. */
  .roster-modal-card { max-width: 1500px; width: 97vw; }

  .roster-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }
  .roster-search-wrap { position: relative; flex: 1 1 320px; min-width: 0; }
  .roster-search-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #a6adbd;
    font-size: 12.5px;
    pointer-events: none;
  }
  .roster-search { width: 100%; padding-left: 32px; }
  .roster-check {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: #3c4658;
    white-space: nowrap;
    cursor: pointer;
  }
  /* The pipeline grid styles this off .data-table, so the roster table needs its
     own rule. Only headers carrying .sortable pick it up — the ceilings table
     inside an expanded row reuses .approver-table and must stay inert. */
  .approver-table th.sortable { cursor: pointer; user-select: none; }
  .approver-table th.sortable:hover { color: #1c2433; }

  .roster-expand-col { width: 26px; color: #a6adbd; font-size: 11px; }
  .roster-row { cursor: pointer; }
  .roster-row:hover { background: #f6f8fc; }
  .roster-row.is-open { background: #eef4ff; }
  /* Switched off by an import that omitted them — still listed, visibly muted. */
  .roster-row.is-inactive td { color: #9aa2b3; }
  .roster-detail-row > td { background: #fbfcfe; padding: 14px 16px; }
  .roster-detail {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .roster-detail-block { flex: 1 1 240px; min-width: 0; }
  .roster-detail-wide { flex: 2 1 420px; }
  .roster-detail-block .approver-section-title { margin-top: 0; }
  .roster-kv { display: flex; gap: 10px; font-size: 12.5px; margin-bottom: 6px; }
  .roster-kv-key { color: #7b8397; flex: 0 0 110px; }
  .roster-kv-val { color: #3c4658; word-break: break-word; }
  .roster-ceiling-table { font-size: 12px; }
  .roster-ceiling-table th { background: #f0f3f8; }

  .approver-table-wrap { overflow-x: auto; border: 1px solid #e4e9f2; border-radius: 6px; }
  .approver-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .approver-table th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    background: #f6f8fc;
    padding: 8px 10px;
    border-bottom: 1px solid #e4e9f2;
    white-space: nowrap;
  }
  .approver-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f3f8;
    color: #3c4658;
  }
  .approver-table tbody tr:last-child td { border-bottom: none; }
  .approver-table th.num, .approver-table td.num { text-align: right; }
  .approver-table td.is-zero { color: #a4472f; font-weight: 600; }

  .approver-pill {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 10px;
    padding: 2px 8px;
  }
  .approver-pill.is-yes { background: #e9f7ef; color: #1e7d4f; }
  .approver-pill.is-no { background: #f1f2f5; color: #7b8397; }

  .approver-table-note {
    font-size: 11.5px;
    line-height: 1.5;
    color: #7b8397;
    margin-top: 6px;
  }

  .approver-warn-list, .approver-change-list {
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    max-height: 260px;
    overflow-y: auto;
  }
  .approver-warn-row {
    display: grid;
    grid-template-columns: 64px minmax(140px, 200px) 1fr;
    gap: 10px;
    font-size: 12px;
    padding: 7px 10px;
    border-bottom: 1px solid #f0f3f8;
  }
  .approver-warn-row:last-child { border-bottom: none; }
  .approver-warn-row-loc { color: #7b8397; }
  .approver-warn-row-who { color: #3c4658; overflow-wrap: anywhere; }
  .approver-warn-row-msg { color: #7a5b12; }
  .approver-warn-more {
    font-size: 11.5px;
    color: #7b8397;
    padding: 8px 10px;
    background: #f6f8fc;
  }

  .approver-change-row {
    display: grid;
    grid-template-columns: 92px minmax(160px, 220px) 120px 1fr;
    gap: 10px;
    align-items: start;
    font-size: 12px;
    padding: 7px 10px;
    border-bottom: 1px solid #f0f3f8;
  }
  .approver-change-row:last-child { border-bottom: none; }
  .approver-change-tag {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-radius: 10px;
    padding: 2px 7px;
    text-align: center;
  }
  .approver-change-tag.tag-new { background: #e9f7ef; color: #1e7d4f; }
  .approver-change-tag.tag-changed { background: #eef4ff; color: #123a71; }
  .approver-change-tag.tag-deactivating { background: #fdeeea; color: #a4472f; }
  .approver-change-tag.tag-reactivating { background: #fff8e6; color: #7a5b12; }
  .approver-change-who { color: #22293a; display: flex; flex-direction: column; overflow-wrap: anywhere; }
  .approver-change-email { font-size: 11px; color: #7b8397; }
  .approver-change-div { color: #7b8397; }
  .approver-change-fields { display: flex; flex-direction: column; gap: 2px; }
  .approver-change-field { color: #3c4658; overflow-wrap: anywhere; }
  .approver-change-field em { color: #7b8397; font-style: normal; }
</style>
